﻿import React, { useState, useEffect, useCallback, memo} from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Shield,
  Globe,
  Cpu,
  Database,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  Music,
  Headphones,
  MessagesSquare,
  Sparkle,
  Zap,
  Camera,
  Mic,
  CheckCircle,
  AlertCircle,
  Loader2,
  LogOut,
  Cloud,
  Volume2,
  Activity,
  Rocket,
  X,
  Eye,
  EyeOff,
  UserCircle,
  Mail,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

import CustomSelect from './CustomSelect';
import { usePlatform } from '@/hooks/usePlatform';
import { BiometricsEnrollPanel } from './biometrics/BiometricsEnrollPanel';
import { useApp, type OperationMode } from '@/contexts/AppContext';
import { useTheme } from '@/contexts/ThemeProvider';
import { THEMES, ThemeId } from '@/contexts/themeRegistry';
import { setUserSetting, STORAGE_TO_SETTING } from '../services/userSettings';
import { VoiceForge } from './VoiceForge';
import { VoiceProviderSwitch } from './VoiceProviderSwitch';
import { MCPSettings } from './MCPSettings';
import { KeyboardSettings } from './settings/KeyboardSettings';
import { APP_VERSION, CHANGELOG, type ChangelogEntry } from '@/lib/version';
import { getSavedKeyStatus, saveServerKeys } from '@/services/settingsKeys';
import { apiFetch } from '@/services/apiClient';
import { Keyboard } from 'lucide-react';
import {
  getSensorPermissionSnapshot,
  isSensorEnabled,
  requestSensorPermission,
  setSensorEnabled,
  SENSOR_ACCESS_CHANGED,
  SENSOR_PERMISSIONS_CHANGED,
  type SensorPermissionState,
} from '@/services/sensorPermissionService';

function buildSidebarGroups(t: any, isZh: boolean) {
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  return [
    {
      label: t.sidebarCore || ui('核心', 'Core'),
      items: [
        { id: 'personal-center', label: t.personalCenter || ui('个人中心', 'Account'), icon: <UserCircle size={16} /> },
        { id: 'general', label: t.sidebarGeneral || ui('通用', 'General'), icon: <Globe size={16} /> },
      ],
    },
    {
      label: t.sidebarAiNeural || ui('AI 与人格', 'AI & Neural'),
      items: [
        { id: 'neural', label: t.neuralEngine || ui('智能体框架', 'Neural Engine'), icon: <BrainCircuit size={16} /> },
        { id: 'llm-providers', label: t.llmProviders || ui('LLM 服务商', 'LLM Providers'), icon: <BrainCircuit size={16} /> },
        { id: 'vision-models', label: t.visionModelSettings || ui('视觉模型', 'Vision Model'), icon: <Camera size={16} /> },
        { id: 'voice-services', label: t.voiceServices || ui('语音服务', 'Voice Services'), icon: <Mic size={16} /> },
      ],
    },
    {
      label: t.sidebarSystem || ui('系统', 'System'),
      items: [
        { id: 'security', label: t.privacySecurity || ui('隐私与安全', 'Security'), icon: <Shield size={16} /> },
        { id: 'hardware', label: t.settingsHardware || ui('硬件权限', 'Hardware'), icon: <Camera size={16} /> },
        { id: 'mcp', label: t.settingsMCP || 'MCP', icon: <Cpu size={16} /> },
        { id: 'keyboard', label: t.keyboardShortcuts || ui('快捷键管理', 'Keyboard Shortcuts'), icon: <Keyboard size={16} /> },
        { id: 'changelog', label: ui('更新日志', 'Changelog'), icon: <Sparkle size={16} /> },
      ],
    },
  ];
}

function SettingsImpl({
  t,
  lang,
  setLang,
  activeSection = 'general',
  onSectionChange,
}: {
  t: any;
  lang: 'en' | 'zh';
  setLang: (l: 'en' | 'zh') => void;
  theme?: ThemeId;
  setTheme?: (theme: ThemeId) => void;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}) {
  const { platform, isElectron } = usePlatform();
  const { operationMode, setOperationMode, user } = useApp();
  // 主题：单一数据源来自 ThemeProvider（web 端此前无主题切换，现已打通）
  const { theme, setTheme, openThemeCenter } = useTheme();
  const [providerStatus, setProviderStatus] = useState<Record<string, { available: boolean; model: string }>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const visibleSection = activeSection === 'computer' || activeSection === 'messaging' ? 'general' : activeSection;
  const isZh = lang !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  // 🎯 P2: Global keyboard shortcuts binding (Ctrl+S for save)
  useEffect(() => {
    const handleSave = () => {
      toast.success(isZh ? '设置已保存' : 'Settings saved');
      // TODO: Replace with actual save logic when needed
    };
    window.addEventListener('keyboard:save-settings', handleSave);
    return () => window.removeEventListener('keyboard:save-settings', handleSave);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadProviderStatus = async () => {
      try {
        const response = await apiFetch('/api/llm/providers');
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Provider status failed (${response.status})`);
        if (!cancelled) setProviderStatus(data.providers || {});
      } catch (err: any) {
        if (!cancelled) {
          toast.error(err?.message || t.failedToLoadProviderStatus || ui('服务商状态加载失败', 'Failed to load provider status'));
        }
      }
    };
    void loadProviderStatus();
    const onKeysChanged = () => { void loadProviderStatus(); };
    window.addEventListener('xf:keys-changed', onKeysChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('xf:keys-changed', onKeysChanged);
    };
  }, []);

  const handleSectionChange = (section: string) => {
    if (onSectionChange) onSectionChange(section);
  };

  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const renderContent = (section: string) => {
    switch (section) {
      case 'personal-center':
        return <ProfileSection t={t} />;
      case 'general':
        return (
          <div className="space-y-8">
            <SettingsSection title={t.language || ui('语言', 'Language')} icon={<Globe size={18} className="text-blue-400" />}>
              <div className="p-8 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08] space-y-6">
                <div>
                  <label className="text-xs font-black uppercase tracking-widest text-white/50 block mb-4">{t.selectLanguage}</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => setLang('en')}
                      className={`p-6 rounded-2xl border text-sm font-bold transition-all flex items-center justify-center gap-3 ${lang === 'en' ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.2)]' : 'bg-white/[0.06] border-white/[0.08] text-white/40 hover:bg-white/10'}`}>
                      {t.englishUS || ui('英文（美国）', 'English (US)')}
                    </button>
                    <button onClick={() => setLang('zh')}
                      className={`p-6 rounded-2xl border text-sm font-bold transition-all flex items-center justify-center gap-3 ${lang === 'zh' ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.2)]' : 'bg-white/[0.06] border-white/[0.08] text-white/40 hover:bg-white/10'}`}>
                      中文 (简体)
                    </button>
                  </div>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection title={t.appearanceThemes || ui('外观与主题', 'Appearance & Themes')} icon={<Sparkle size={18} className="text-celestial-saturn" />}>
              <div className="p-8 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08] space-y-8">
                <div>
                  <label className="text-xs font-black uppercase tracking-widest text-white/50 block mb-4">{t.selectMatrixVariant || ui('选择全局主题变体', 'Select Global Matrix Variant')}</label>
                  <div className="grid grid-cols-3 gap-4">
                    {THEMES.map((themeItem) => (
                      <button key={themeItem.id} onClick={() => setTheme(themeItem.id)}
                        className={`flex flex-col items-center gap-3 p-4 rounded-2xl border transition-all text-center ${theme === themeItem.id ? 'bg-white/10 border-white/20 shadow-lg' : 'border-white/[0.08] hover:bg-white/[0.06]'}`}>
                        <div
                          className={`w-16 h-16 rounded-full shadow-lg ${theme === themeItem.id ? 'ring-2 ring-white/50 ring-offset-2 ring-offset-black' : ''}`}
                          style={{ background: `conic-gradient(from 0deg, ${themeItem.swatch.join(', ')})` }}
                        />
                        <span className={`text-xs font-black uppercase tracking-widest ${theme === themeItem.id ? 'text-white' : 'text-white/60'}`}>
                          {themeItem.name}
                          {themeItem.tag && <span className="ml-1 text-white/40">方案{themeItem.tag}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                  <button onClick={openThemeCenter}
                    className="w-full mt-2 py-3 rounded-2xl border border-celestial-saturn/30 bg-celestial-saturn/10 text-sm font-black text-celestial-saturn hover:bg-celestial-saturn/20 transition-colors flex items-center justify-center gap-2">
                    <Sparkle size={16} /> {t.openThemeCenter || ui('打开主题中心预览全部', 'Open Theme Center')}
                  </button>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection title={ui('神经合成面板', 'Neural Synthesis Panel')} icon={<Activity size={18} className="text-green-400" />}>
              <div className="p-6 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <SettingsItem
                    label={ui('显示神经合成监控', 'Show Neural Synthesis Monitor')}
                    desc={ui('系统资源、AI推理、延迟和传感器数据', 'System resources, AI inference, latency and sensor data')}
                    storageKey="xf_neural_synthesis_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示智能穿戴', 'Show Smart Wearable')}
                    desc={ui('小米穿戴健康数据（心率、血氧、睡眠）', 'Xiaomi wearable health data')}
                    storageKey="xf_wearable_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示智能车控', 'Show Smart Car Control')}
                    desc={ui('比亚迪车辆状态与控制', 'BYD vehicle status and controls')}
                    storageKey="xf_carcontrol_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示米家设备', 'Show Mijia Devices')}
                    desc={ui('米家IoT设备（空调、温湿度计等）', 'Mijia IoT devices')}
                    storageKey="xf_mijia_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示运行日志', 'Show Runtime Log')}
                    desc={ui('系统运行日志（实时刷新）', 'System runtime log')}
                    storageKey="xf_runtime_log_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示日历', 'Show Calendar')}
                    desc={ui('日期、天气与月历', 'Date, weather and monthly calendar')}
                    storageKey="xf_calendar_enabled"
                    t={t}
                  />
                  <SettingsItem
                    label={ui('显示学习流', 'Show Learning Flow')}
                    desc={ui('学习流（计划与知识沉淀）', 'Learning flow')}
                    storageKey="xf_learning_flow_enabled"
                    t={t}
                  />
                </div>
                <HiddenDevicesManager t={t} />
              </div>
            </SettingsSection>
          </div>
        );
      case 'neural':
        return (
          <div className="space-y-8">
            <SettingsSection title={t.agentFramework || ui('智能体框架（小枫 协议）', 'Agent Framework (小枫 Protocol)')} icon={<BrainCircuit size={18} className="text-celestial-saturn" />}>
              <div className="space-y-6">
                <AutonomousSettingsPanel t={t} operationMode={operationMode} setOperationMode={setOperationMode} />
              </div>
            </SettingsSection>
          </div>
        );
      case 'voice':
        return <VoiceForge t={t} />;
      case 'llm-providers':
        return <LLMProvidersPage t={t} providerStatus={providerStatus} />;
      case 'vision-models':
        return <VisionModelPage t={t} />;
      case 'voice-services':
        return <VoiceServicesPage t={t} />;
      case 'security':
        return (
          <div className="space-y-8">
            <SettingsSection title={t.privacySecurity || ui('隐私与安全', 'Privacy & Security')} icon={<Shield size={18} className="text-celestial-mars" />}>
              <SettingsItem label={t.localEncryption || ui('本地加密', 'Local Encryption')} desc={t.localEncryptionDesc || ui('加密存储在本地磁盘上的所有智能体数据。', 'Encrypt all Agent data stored on your local disk.')} storageKey="xf_sec_local_encryption" t={t} />
              <SettingsItem label={t.anonymousMode || ui('匿名模式', 'Anonymous Mode')} desc={t.anonymousModeDesc || ui('在协作网络中隐藏你的节点 ID。', 'Hide your node ID from the collaborative network.')} storageKey="xf_sec_anonymous_mode" t={t} />
              <SettingsItem label={t.biometricLock || ui('生物识别锁', 'Biometric Lock')} desc={t.biometricLockDesc || ui('生成智能体时要求指纹或人脸验证。', 'Require fingerprint or face ID for Agent generation.')} storageKey="xf_sec_biometric_lock" t={t} />
            </SettingsSection>
            {isElectron && (
              <SettingsSection title={t.desktopNodeRuntime || ui('桌面节点运行时', 'Desktop Node Runtime')} icon={<Database size={18} className="text-celestial-jupiter" />}>
                <div className="p-4 bg-celestial-jupiter/10 rounded-2xl border border-celestial-jupiter/20 space-y-2 mb-4">
                  <div className="flex justify-between items-center text-sm"><span className="text-white/60">{t.platform || ui('平台', 'Platform')}:</span><span className="font-mono text-celestial-jupiter uppercase">{platform}</span></div>
                  <div className="flex justify-between items-center text-sm"><span className="text-white/60">{t.nodeStatus || ui('节点状态', 'Node Status')}:</span><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /><span className="font-bold text-green-500 underline decoration-green-500/20 underline-offset-4">{t.nodeActive || ui('运行中', 'ACTIVE')}</span></div></div>
                </div>
                <SettingsItem label={t.hardwareAcceleration || ui('硬件加速', 'Hardware Acceleration')} desc={t.hardwareAccelerationDesc || ui('使用 GPU 加速核心推理。', 'Use GPU for neural core inference.')} storageKey="xf_sec_hw_accel" t={t} />
                <SettingsItem label={t.systemTrayMode || ui('系统托盘模式', 'System Tray Mode')} desc={t.systemTrayModeDesc || ui('让小枫 在后台保持运行。', 'Keep 小枫 running in the background.')} storageKey="xf_sec_system_tray" t={t} />
              </SettingsSection>
            )}

            <SettingsSection title="生物特征录入" icon={<Shield size={18} className="text-amber-400" />}>
              <div className="p-6 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08]">
                <BiometricsEnrollPanel />
              </div>
            </SettingsSection>
          </div>
        );
      case 'hardware':
        return <HardwareSettings t={t} />;
      case 'mcp':
        return <MCPSettings t={t} />;
      case 'keyboard':
        return <KeyboardSettings />;
      case 'changelog':
        return <ChangelogPanel t={t} lang={lang} />;
      default:
        return null;
    }
  };

  return (
    <div className="xf-surface flex h-full overflow-hidden">
      {/* ── 侧边栏 — 始终可见，响应式宽度 + 折叠动画 ── */}
      <div className="flex-shrink-0 border-r border-white/[0.08] bg-white/[0.04] flex flex-col min-h-0 w-48 lg:w-56 xl:w-64">
        {/* ── 用户卡片 ── */}
        <div className="px-3 pt-4 pb-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-celestial-saturn/40 to-celestial-saturn/10 border border-celestial-saturn/25 flex items-center justify-center text-sm flex-shrink-0">
              {(user?.avatar as string) ? (
                <img src={user.avatar as string} className="w-full h-full rounded-full object-cover" alt="" />
              ) : (
                <span className="text-white/80 font-black text-xs">{String(user?.displayName || user?.username || 'U')[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-black text-white/80 truncate">{user?.displayName || user?.username || ui('用户', 'User')}</p>
              <p className="text-[10px] text-white/30 truncate">{(user?.role as string) === 'super_admin' ? '超级管理员' : (user?.role as string) === 'admin' ? '管理员' : user?.role || ''}</p>
            </div>
            <button
              onClick={async () => {
                try {
                  await apiFetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
                  localStorage.removeItem('xf_auth_token');
                  window.location.reload();
                } catch {
                  localStorage.removeItem('xf_auth_token');
                  window.location.reload();
                }
              }}
              className="w-7 h-7 rounded-lg bg-red-500/[0.08] border border-red-500/[0.12] flex items-center justify-center text-red-400/60 hover:text-red-300 hover:bg-red-500/[0.15] transition-all flex-shrink-0"
              title={t?.signOut || ui('退出登录', 'Sign Out')}
            >
              <LogOut size={12} />
            </button>
          </div>
        </div>

        {/* ── 设置标题 ── */}
        <div className="px-4 pt-3 pb-2">
          <h2 className="text-[10px] font-black uppercase tracking-widest text-white/35">{t.settings || ui('设置', 'Settings')}</h2>
        </div>

        <div className="flex-1 px-2 pb-3 space-y-0.5 overflow-y-auto custom-scrollbar min-h-0">
          {buildSidebarGroups(t, isZh).map(group => {
            const isCollapsed = collapsedGroups.has(group.label);
            return (
              <div key={group.label} className="mb-2">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="flex w-full items-center gap-1 px-2 py-1 text-xs font-black uppercase tracking-widest text-white/45 transition-colors hover:text-white/70"
                >
                  <ChevronDown size={9} className={`transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                  {group.label}
                </button>
                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden space-y-0.5"
                    >
                      {group.items.map(item => (
                        <SidebarItem
                          key={item.id}
                          active={visibleSection === item.id}
                          onClick={() => handleSectionChange(item.id)}
                          icon={item.icon}
                          label={item.label}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 内容区 — max-w 约束 + 增强过渡动画 + overscroll-contain ── */}
      <div className="flex-1 min-w-0 relative overflow-hidden">
        <div className="absolute inset-0 overflow-y-auto custom-scrollbar p-5 md:p-6 overscroll-contain">
          <div className="max-w-3xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={visibleSection}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {renderContent(visibleSection)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function HardwareSettings({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [micStatus, setMicStatus] = useState<SensorPermissionState>('unknown');
  const [camStatus, setCamStatus] = useState<SensorPermissionState>('unknown');
  const [micEnabled, setMicEnabled] = useState(() => isSensorEnabled('microphone'));
  const [camEnabled, setCamEnabled] = useState(() => isSensorEnabled('camera'));
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    let disposed = false;

    const refresh = async () => {
      const snapshot = await getSensorPermissionSnapshot();
      if (disposed) return;
      setMicStatus(snapshot.microphone);
      setCamStatus(snapshot.camera);
    };

    const onSensorChange = (event: Event) => {
      const detail = (event as CustomEvent<Partial<Record<'microphone' | 'camera', SensorPermissionState>>>).detail;
      if (detail?.microphone) setMicStatus(detail.microphone);
      if (detail?.camera) setCamStatus(detail.camera);
      if (!detail?.microphone && !detail?.camera) void refresh();
    };

    const refreshAccess = () => {
      setMicEnabled(isSensorEnabled('microphone'));
      setCamEnabled(isSensorEnabled('camera'));
    };

    const onSensorAccessChange = (event: Event) => {
      const detail = (event as CustomEvent<Partial<Record<'microphone' | 'camera', boolean>>>).detail;
      if (typeof detail?.microphone === 'boolean') setMicEnabled(detail.microphone);
      if (typeof detail?.camera === 'boolean') setCamEnabled(detail.camera);
      if (typeof detail?.microphone !== 'boolean' && typeof detail?.camera !== 'boolean') refreshAccess();
    };

    const onStorageChange = (event: StorageEvent) => {
      if (event.key === 'xf_mic_enabled' || event.key === 'xf_camera_enabled') refreshAccess();
    };

    void refresh();
    refreshAccess();
    window.addEventListener(SENSOR_PERMISSIONS_CHANGED, onSensorChange);
    window.addEventListener(SENSOR_ACCESS_CHANGED, onSensorAccessChange);
    window.addEventListener('storage', onStorageChange);
    window.addEventListener('visibilitychange', refresh);
    window.addEventListener('visibilitychange', refreshAccess);
    return () => {
      disposed = true;
      window.removeEventListener(SENSOR_PERMISSIONS_CHANGED, onSensorChange);
      window.removeEventListener(SENSOR_ACCESS_CHANGED, onSensorAccessChange);
      window.removeEventListener('storage', onStorageChange);
      window.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('visibilitychange', refreshAccess);
    };
  }, []);

  const requestPermissions = async (type: 'mic' | 'camera') => {
    setIsRequesting(true);
    try {
      const kind = type === 'mic' ? 'microphone' : 'camera';
      const result = await requestSensorPermission(kind);
      if (type === 'mic') setMicStatus(result.state);
      if (type === 'camera') setCamStatus(result.state);
      if (!result.ok) {
        throw new Error(result.error || ui('请在系统权限中打开访问', 'Please enable access in system permissions.'));
      }

      toast.success(type === 'mic' ? (t.micAccessSynced || ui('麦克风权限已同步。', 'Microphone access synchronized.')) : (t.camAccessSynced || ui('摄像头权限已同步。', 'Camera access synchronized.')));
      return true;
    } catch (err: any) {
      toast.error(`${t.sensorLinkFailed || ui('传感器连接失败', 'Sensor link failed')}: ${err.message}`);
      return false;
    } finally {
      setIsRequesting(false);
    }
  };

  const handleSensorToggle = async (type: 'mic' | 'camera', enabled: boolean) => {
    const kind = type === 'mic' ? 'microphone' : 'camera';
    setSensorEnabled(kind, enabled);
    if (type === 'mic') setMicEnabled(enabled);
    if (type === 'camera') setCamEnabled(enabled);

    if (!enabled) {
      toast.success(type === 'mic'
        ? (t.audioReceptorsDisabled || ui('音频输入已停用。', 'Audio input disabled.'))
        : (t.visualCortexDisabled || ui('视觉感知已停用。', 'Visual perception disabled.')));
      return;
    }

    const ok = await requestPermissions(type);
    if (!ok) {
      setSensorEnabled(kind, false);
      if (type === 'mic') setMicEnabled(false);
      if (type === 'camera') setCamEnabled(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <SettingsSection title={t.hardwareSensorNetwork || ui('硬件传感器网络', 'Hardware Sensor Network')} icon={<Camera size={18} className="text-celestial-saturn" />}>
        <p className="text-sm text-white/40 mb-8 max-w-xl">
          {t.hardwareSensorNetworkDesc || ui('小枫 需要访问物理传感器来进行现实上下文感知和生物识别验证。所有数据都会在你的本地节点处理。', '小枫 requires access to your physical sensors for real-world contextual awareness and biometric verification. All data is processed locally on your node.')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <HardwareCapCard
            icon={<Mic size={24} />}
            label={t.audioReceptors || ui('音频输入', 'Audio Receptors')}
            desc={t.audioReceptorsDesc || ui('启用语音识别和声音克隆相关能力。', 'Enable neural speech recognition and voice cloning.')}
            status={micStatus}
            enabled={micEnabled}
            onToggle={(enabled) => void handleSensorToggle('mic', enabled)}
            disabled={isRequesting}
            t={t}
          />
          <HardwareCapCard
            icon={<Camera size={24} />}
            label={t.visualCortex || ui('视觉感知', 'Visual Cortex')}
            desc={t.visualCortexDesc || ui('启用多模态视觉和手势控制。', 'Enable multimodal vision and gesture control.')}
            status={camStatus}
            enabled={camEnabled}
            onToggle={(enabled) => void handleSensorToggle('camera', enabled)}
            disabled={isRequesting}
            t={t}
          />
        </div>

        <div className="mt-12 p-6 glass-dark rounded-[2rem] border border-white/[0.08] space-y-4">
           <div className="flex items-center gap-3">
              <Shield className="text-celestial-saturn" size={20} />
              <h4 className="text-sm font-bold uppercase tracking-tight text-white">{t.privacyAssurance || ui('隐私保证', 'Privacy Assurance')}</h4>
           </div>
           <p className="text-xs text-white/55 leading-relaxed italic">
             {t.privacyAssuranceText || ui('协议默认强制本地处理。没有你的明确确认，视觉和听觉数据流不会离开你的本地主权节点。', 'Our protocol strictly enforces local-only processing. Your visual and auditory data streams are never transmitted outside your sovereign mesh node without direct user-signed override.')}
           </p>
        </div>
      </SettingsSection>
    </div>
  );
}

function HardwareCapCard({ icon, label, desc, status, enabled, onToggle, disabled, t }: {
  icon: React.ReactNode,
  label: string,
  desc: string,
  status: SensorPermissionState,
  enabled: boolean,
  onToggle: (enabled: boolean) => void,
  disabled: boolean,
  t: any
}) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const isUnavailable = status === 'unavailable';
  const isLinked = enabled && status === 'granted';
  const isBlocked = status === 'denied';
  return (
    <div className="p-8 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08] flex flex-col justify-between gap-6 group hover:border-white/10 transition-all">
      <div className="space-y-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
          isLinked ? 'bg-celestial-saturn text-black' : 'bg-white/[0.06] text-white/40'
        }`}>
          {icon}
        </div>
        <div>
          <h4 className="text-lg font-bold text-white">{label}</h4>
          <p className="text-xs text-white/40 leading-relaxed mt-1">{desc}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
           {isLinked ? (
             <div className="flex items-center gap-1.5 text-celestial-saturn text-xs font-black uppercase tracking-widest">
               <CheckCircle size={12} />
                {t.linked || ui('已连接', 'Linked')}
             </div>
           ) : isBlocked ? (
             <div className="flex items-center gap-1.5 text-red-500 text-xs font-black uppercase tracking-widest">
               <AlertCircle size={12} />
                {t.blocked || ui('已阻止', 'Blocked')}
             </div>
           ) : isUnavailable ? (
             <div className="flex items-center gap-1.5 text-white/35 text-xs font-black uppercase tracking-widest">
               <AlertCircle size={12} />
               {t.unavailable || ui('不可用', 'Unavailable')}
             </div>
           ) : !enabled ? (
              <div className="text-xs font-black uppercase tracking-widest text-white/45">{t.disabled || ui('已停用', 'Disabled')}</div>
           ) : (
              <div className="text-xs font-black uppercase tracking-widest text-white/45">{t.awaitingAccess || ui('等待授权', 'Awaiting Access')}</div>
           )}
        </div>

        <div className="flex items-center gap-2">
          {enabled && status !== 'granted' && !isUnavailable && (
            <Button
              onClick={() => onToggle(true)}
              disabled={disabled}
              className="bg-white/[0.06] hover:bg-white/10 border border-white/10 text-xs font-black uppercase tracking-widest px-4 h-9 rounded-xl"
            >
              {status === 'denied' ? (t.retryLink || ui('重新连接', 'Retry Link')) : (t.authorize || ui('授权', 'Authorize'))}
            </Button>
          )}
          <button
            type="button"
            onClick={() => onToggle(!enabled)}
            disabled={disabled || isUnavailable}
            aria-pressed={enabled}
            aria-label={enabled ? ui(`停用${label}`, `Disable ${label}`) : ui(`启用${label}`, `Enable ${label}`)}
            className={`relative h-9 w-16 rounded-full border transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
              enabled
                ? 'border-celestial-saturn/40 bg-celestial-saturn/25 shadow-[0_0_18px_rgba(255,204,0,0.18)]'
                : 'border-white/10 bg-white/[0.06] hover:bg-white/10'
            }`}
          >
            <span
              className={`absolute top-1 h-7 w-7 rounded-full transition-all ${
                enabled
                  ? 'left-8 bg-celestial-saturn'
                  : 'left-1 bg-white/35'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

function SidebarItem({ active, onClick, icon, label }: { active: boolean, onClick: () => void, icon: React.ReactNode, label: string }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 ${active ? 'border border-white/10 bg-white/[0.075] text-white' : 'border border-transparent text-white/55 hover:bg-white/[0.045] hover:text-white/75'}`}
    >
      <div className={`flex-shrink-0 w-4 h-4 flex items-center justify-center ${active ? 'text-celestial-saturn' : 'text-current'}`}>{icon}</div>
      <span className="text-[12px] font-bold uppercase tracking-tight truncate">{label}</span>
      {active && <div className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-celestial-saturn" />}
    </button>
  );
}

function LLMProviderRow({ icon, label, providerId, models, placeholder, disabled = false, serverKey, t }: {
  icon: React.ReactNode; label: string; providerId: string; models: string[];
  placeholder: string; disabled?: boolean; serverKey: string; t?: any;
}) {
  const { aiConfig, updateAIConfig } = useApp();
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [keyValue, setKeyValue] = useState(() => {
    try { return localStorage.getItem(`xf_${providerId}_key`) || ''; } catch { return ''; }
  });
  const [saved, setSaved] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleTestKey = async () => {
    if (!keyValue.trim()) return;
    setTesting(true);
    try {
      const res = await apiFetch('/api/llm/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: providerId, apiKey: keyValue.trim(), model }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(data.message || t?.llmTestSuccess || ui('连接成功，API Key 有效!', 'Connection successful! API key is valid.'));
        // Show details if available
        if (data.details) {
          console.log('[API Test Details]', data.details);
        }
      } else {
        const errorMsg = data.error || data.message || t?.llmTestFailed || ui('连接失败', 'Connection failed');
        toast.error(errorMsg);
        // Log detailed error for debugging
        if (data.details) {
          console.error('[API Test Error Details]', data.details);
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || t?.llmTestError || ui('测试请求失败', 'Test request failed');
      toast.error(errorMessage);
      console.error('[API Test Exception]', err);
    } finally {
      setTesting(false);
    }
  };

  const savedModels = (() => {
    try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
  })();
  const [model, setModel] = useState(() => {
    return savedModels[providerId] || models[0];
  });

  useEffect(() => {
    getSavedKeyStatus()
      .then(data => setServerConfigured(!!data[serverKey]))
      .catch(() => {});
  }, [serverKey]);

  const handleRemoveKey = () => {
    saveServerKeys({ [serverKey]: '' }).then(() => {
      localStorage.removeItem(`xf_${providerId}_key`);
      setServerConfigured(false);
      setKeyValue('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeyRemoved || ui('API Key 已移除', 'API key removed'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToRemoveKey || ui('密钥移除失败', 'Failed to remove key')));
  };

  const handleSaveKey = () => {
    if (!keyValue.trim()) return;
    saveServerKeys({ [serverKey]: keyValue.trim() }).then(() => {
      localStorage.setItem(`xf_${providerId}_key`, keyValue.trim());
      setServerConfigured(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeySaved || ui('API Key 已保存', 'API key saved'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToSaveKey || ui('密钥保存失败', 'Failed to save key')));
  };

  const syncToServer = (models: Record<string, string>) => {
    apiFetch('/api/preferences/llm', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: aiConfig.provider, models }),
      credentials: 'include',
    }).catch(() => {});
  };

  const handleModelChange = (m: string) => {
    setModel(m);
    const allModels = (() => {
      try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
    })();
    allModels[providerId] = m;
    localStorage.setItem('xf_llm_models', JSON.stringify(allModels));
    syncToServer(allModels);
    if (aiConfig.provider === providerId) {
      updateAIConfig({ model: m });
    }
  };

  const handleUseAsLLM = () => {
    updateAIConfig({ provider: providerId, model });
    // 同步主引擎到后端数据库
    const allModels = (() => {
      try { return JSON.parse(localStorage.getItem('xf_llm_models') || '{}'); } catch { return {}; }
    })();
    allModels[providerId] = model;
    localStorage.setItem('xf_llm_models', JSON.stringify(allModels));
    apiFetch('/api/preferences/llm', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider: providerId, models: allModels }),
      credentials: 'include',
    }).catch(() => {});
  };

  return (
    <div className="p-6 bg-white/[0.06] rounded-3xl border border-white/[0.08] space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-white/[0.06] rounded-lg">{icon}</div>
        <label className="text-xs font-black uppercase tracking-widest text-white/50">{label}</label>
        {serverConfigured && <span className="text-xs px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full font-bold">{t?.configured || ui('已配置', 'CONFIGURED')}</span>}
        {saved && <CheckCircle size={14} className="text-green-400 ml-auto" />}
      </div>
      <div className="flex gap-3">
        <div className="relative flex-1">
          <input
            disabled={disabled}
            type={showKey ? 'text' : 'password'}
            value={keyValue}
            onChange={e => setKeyValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveKey()}
            placeholder={serverConfigured && !keyValue ? (t?.keySavedOnServer || ui('密钥已保存在服务器', 'Key saved on server')) : placeholder}
            className="w-full bg-black/40 border border-white/10 rounded-xl p-4 pr-16 text-white font-mono text-sm outline-none focus:border-celestial-saturn/50 transition-colors disabled:opacity-50"
          />
          <div className="absolute right-2 top-2 flex gap-1">
            <button type="button" onClick={() => setShowKey(!showKey)}
              className="h-10 px-2 bg-white/[0.06] hover:bg-white/10 text-xs font-bold uppercase border border-white/[0.08] rounded-lg">
              {showKey ? (t?.hide || ui('隐藏', 'Hide')) : (t?.show || ui('显示', 'Show'))}
            </button>
          </div>
        </div>
        <Button
          onClick={handleSaveKey}
          disabled={disabled || !keyValue.trim()}
          className="h-[56px] px-4 bg-celestial-saturn text-black rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed hover:bg-celestial-saturn/90 transition-all"
        >
          {t?.save || ui('保存', 'Save')}
        </Button>
        <Button
          onClick={handleRemoveKey}
          disabled={disabled || (!keyValue && !serverConfigured)}
          className="h-[56px] px-4 bg-red-500/10 border border-red-500/20 rounded-xl text-xs font-black uppercase tracking-widest text-red-400 hover:bg-red-500/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        >
          {t?.remove || ui('移除', 'Remove')}
        </Button>
        <Button
          onClick={handleTestKey}
          disabled={disabled || !keyValue.trim() || testing}
          className="h-[56px] px-4 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs font-black uppercase tracking-widest text-blue-400 hover:bg-blue-500/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        >
          {testing ? (ui('测试中...', 'Testing...')) : (t?.testConnection || ui('测试连接', 'Test'))}
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-[12px] font-black uppercase text-white/55 tracking-wider whitespace-nowrap">{t?.model || ui('模型', 'Model')}</label>
        <select
          value={model}
          onChange={e => handleModelChange(e.target.value)}
          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono font-bold outline-none focus:border-celestial-saturn/50 text-white cursor-pointer"
        >
          {models.map(m => <option key={m} value={m} className="bg-black text-white">{m}</option>)}
        </select>
        {aiConfig.provider === providerId ? (
          <span className="text-xs px-2 py-0.5 bg-celestial-saturn/10 border border-celestial-saturn/20 text-celestial-saturn rounded-full font-bold whitespace-nowrap">{t?.activeBadge || ui('当前', 'ACTIVE')}</span>
        ) : (
          <Button
            onClick={handleUseAsLLM}
            disabled={!keyValue.trim() && !serverConfigured}
            className="h-9 px-3 bg-celestial-saturn text-black rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 hover:bg-celestial-saturn/90 transition-all"
          >
            {ui('设为LLM', 'Use as LLM')}
          </Button>
        )}
      </div>
    </div>
  );
}

function VisionProviderRow({ icon, label, providerId, models, placeholder, disabled = false, serverKey, t }: {
  icon: React.ReactNode; label: string; providerId: string; models: string[];
  placeholder: string; disabled?: boolean; serverKey: string; t?: any;
}) {
  const { visionConfig, updateVisionConfig } = useApp();
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [keyValue, setKeyValue] = useState(() => {
    try { return localStorage.getItem(`xf_vision_${providerId}_key`) || ''; } catch { return ''; }
  });
  const [saved, setSaved] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const savedModels = (() => {
    try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
  })();
  const [model, setModel] = useState(() => savedModels[providerId] || models[0]);

  useEffect(() => {
    getSavedKeyStatus()
      .then(data => setServerConfigured(!!data[serverKey]))
      .catch(() => {});
  }, [serverKey]);

  const handleSaveKey = () => {
    if (!keyValue.trim()) return;
    saveServerKeys({ [serverKey]: keyValue.trim() }).then(() => {
      localStorage.setItem(`xf_vision_${providerId}_key`, keyValue.trim());
      setServerConfigured(true);
      if (visionConfig.provider === providerId) {
        updateVisionConfig({ apiKey: keyValue.trim(), model });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeySaved || ui('API Key 已保存', 'API key saved'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToSaveKey || ui('密钥保存失败', 'Failed to save key')));
  };

  const handleRemoveKey = () => {
    saveServerKeys({ [serverKey]: '' }).then(() => {
      localStorage.removeItem(`xf_vision_${providerId}_key`);
      setServerConfigured(false);
      setKeyValue('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeyRemoved || ui('API Key 已移除', 'API key removed'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToRemoveKey || ui('密钥移除失败', 'Failed to remove key')));
  };

  const handleModelChange = (m: string) => {
    setModel(m);
    const allModels = (() => {
      try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
    })();
    allModels[providerId] = m;
    localStorage.setItem('xf_vision_models', JSON.stringify(allModels));
    if (visionConfig.provider === providerId) {
      updateVisionConfig({ model: m });
    } else {
      apiFetch('/api/preferences/vision', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: visionConfig.provider, model: visionConfig.model, models: allModels }),
        credentials: 'include',
      }).catch(() => {});
    }
  };

  return (
    <div className="p-6 bg-white/[0.06] rounded-3xl border border-white/[0.08] space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-white/[0.06] rounded-lg">{icon}</div>
        <label className="text-xs font-black uppercase tracking-widest text-white/50">{label}</label>
        {serverConfigured && <span className="text-xs px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full font-bold">{t?.configured || ui('已配置', 'CONFIGURED')}</span>}
        {saved && <CheckCircle size={14} className="text-green-400 ml-auto" />}
      </div>
      <div className="flex gap-3">
        <div className="relative flex-1">
          <input
            disabled={disabled}
            type={showKey ? 'text' : 'password'}
            value={keyValue}
            onChange={e => setKeyValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSaveKey()}
            placeholder={serverConfigured && !keyValue ? (t?.keySavedOnServer || ui('密钥已保存在服务器', 'Key saved on server')) : placeholder}
            className="w-full bg-black/40 border border-white/10 rounded-xl p-4 pr-16 text-white font-mono text-sm outline-none focus:border-cyan-300/50 transition-colors disabled:opacity-50"
          />
          <div className="absolute right-2 top-2 flex gap-1">
            <button type="button" onClick={() => setShowKey(!showKey)}
              className="h-10 px-2 bg-white/[0.06] hover:bg-white/10 text-xs font-bold uppercase border border-white/[0.08] rounded-lg">
              {showKey ? (t?.hide || ui('隐藏', 'Hide')) : (t?.show || ui('显示', 'Show'))}
            </button>
          </div>
        </div>
        <Button
          onClick={handleSaveKey}
          disabled={disabled || !keyValue.trim()}
          className="h-[56px] px-4 bg-cyan-300 text-black rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-200 transition-all"
        >
          {t?.save || ui('保存', 'Save')}
        </Button>
        <Button
          onClick={handleRemoveKey}
          disabled={disabled || (!keyValue && !serverConfigured)}
          className="h-[56px] px-4 bg-red-500/10 border border-red-500/20 rounded-xl text-xs font-black uppercase tracking-widest text-red-400 hover:bg-red-500/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        >
          {t?.remove || ui('移除', 'Remove')}
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-[12px] font-black uppercase text-white/55 tracking-wider whitespace-nowrap">{t?.model || ui('模型', 'Model')}</label>
        <input
          type="text"
          value={model}
          onChange={e => handleModelChange(e.target.value)}
          list={`vision-models-${providerId}`}
          placeholder={models[0]}
          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono font-bold outline-none focus:border-cyan-300/50"
        />
        <datalist id={`vision-models-${providerId}`}>
          {models.map(m => <option key={m} value={m} />)}
        </datalist>
        {visionConfig.provider === providerId && (
          <span className="text-xs px-2 py-0.5 bg-cyan-300/10 border border-cyan-300/20 text-cyan-200 rounded-full font-bold whitespace-nowrap">{t?.activeBadge || ui('当前', 'ACTIVE')}</span>
        )}
      </div>
    </div>
  );
}

function VisionRelayProviderRow({ t }: { t?: any }) {
  const { visionConfig, updateVisionConfig } = useApp();
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [apiKey, setApiKey] = useState(() => {
    try { return localStorage.getItem('xf_relay_key') || ''; } catch { return ''; }
  });
  const [baseUrl, setBaseUrl] = useState(() => {
    try { return localStorage.getItem('xf_relay_url') || 'http://localhost:8000/v1'; } catch { return 'http://localhost:8000/v1'; }
  });
  const savedModels = (() => {
    try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
  })();
  const [model, setModel] = useState(() => savedModels.relay || 'qwen2.5-vl-7b-instruct');
  const [serverConfigured, setServerConfigured] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSavedKeyStatus()
      .then(data => setServerConfigured(!!data.RELAY_API_KEY && !!data.RELAY_BASE_URL))
      .catch(() => {});
  }, []);

  const persistModel = (nextModel: string) => {
    setModel(nextModel);
    const allModels = (() => {
      try { return JSON.parse(localStorage.getItem('xf_vision_models') || '{}'); } catch { return {}; }
    })();
    allModels.relay = nextModel;
    localStorage.setItem('xf_vision_models', JSON.stringify(allModels));
    if (visionConfig.provider === 'relay') updateVisionConfig({ model: nextModel });
  };

  const handleSave = () => {
    if (!apiKey.trim() || !baseUrl.trim()) return;
    saveServerKeys({ RELAY_API_KEY: apiKey.trim(), RELAY_BASE_URL: baseUrl.trim() }).then(() => {
      localStorage.setItem('xf_relay_key', apiKey.trim());
      localStorage.setItem('xf_relay_url', baseUrl.trim());
      setServerConfigured(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeySaved || ui('API Key 已保存', 'API key saved'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToSaveKey || ui('保存失败', 'Failed to save')));
  };

  const handleUse = () => {
    updateVisionConfig({ provider: 'relay', model });
  };

  return (
    <div className="p-6 bg-white/[0.06] rounded-3xl border border-white/[0.08] space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-white/[0.06] rounded-lg"><Globe size={18} className="text-cyan-400" /></div>
        <label className="text-xs font-black uppercase tracking-widest text-white/50">OpenAI-Compatible Vision</label>
        {serverConfigured && <span className="text-xs px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full font-bold">{ui('已配置', 'CONFIGURED')}</span>}
        {visionConfig.provider === 'relay' && <span className="text-xs px-2 py-0.5 bg-cyan-300/10 border border-cyan-300/20 text-cyan-200 rounded-full font-bold">{t?.activeBadge || ui('当前', 'ACTIVE')}</span>}
        {saved && <CheckCircle size={14} className="text-green-400 ml-auto" />}
      </div>
      <div className="grid grid-cols-1 gap-3">
        <input
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          placeholder="API Key"
          className="bg-black/40 border border-white/10 rounded-xl p-4 text-white font-mono text-sm outline-none focus:border-cyan-400/50 transition-colors"
        />
        <input
          type="text"
          value={baseUrl}
          onChange={e => setBaseUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          placeholder="http://localhost:8000/v1"
          className="bg-black/40 border border-white/10 rounded-xl p-4 text-white font-mono text-sm outline-none focus:border-cyan-400/50 transition-colors"
        />
      </div>
      <div className="flex items-center gap-3">
        <label className="text-[12px] font-black uppercase text-white/55 tracking-wider whitespace-nowrap">{t?.model || ui('模型', 'Model')}</label>
        <input
          type="text"
          value={model}
          onChange={e => persistModel(e.target.value)}
          list="vision-relay-models"
          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono font-bold outline-none focus:border-cyan-400/50"
        />
        <datalist id="vision-relay-models">
          {['qwen2.5-vl-7b-instruct', 'minicpm-v-4_5', 'internvl3_5-8b', 'glm-4.1v-9b-thinking'].map(m => <option key={m} value={m} />)}
        </datalist>
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          disabled={!apiKey.trim() || !baseUrl.trim()}
          className="h-[44px] px-5 bg-cyan-600 text-white rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 hover:bg-cyan-500 transition-all"
        >
          {t?.save || ui('保存', 'Save')}
        </Button>
        <Button
          onClick={handleUse}
          disabled={!model.trim()}
          className="h-[44px] px-5 bg-cyan-300 text-black rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 hover:bg-cyan-200 transition-all"
        >
          {ui('设为视觉', 'Use as Vision')}
        </Button>
      </div>
      <p className="text-[12px] text-white/45 leading-relaxed">
        {ui('用于 vLLM、SGLang、Xinference 或其他兼容 OpenAI Chat Completions 的本地视觉服务。', 'Use this for vLLM, SGLang, Xinference, or any local OpenAI-compatible vision endpoint.')}
      </p>
    </div>
  );
}

function ProactiveVoiceToggle() {
  const storageKey = 'xf_allow_proactive_voice';
  const [enabled, setEnabled] = useState(() => localStorage.getItem(storageKey) === 'true');

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(storageKey, String(next));
    window.dispatchEvent(new CustomEvent('xf:setting-changed', {
      detail: { key: storageKey, value: next },
    }));
  };

  return (
    <button
      onClick={toggle}
      className={`w-11 h-6 rounded-full transition-all relative ${enabled ? 'bg-celestial-saturn' : 'bg-white/10 border border-white/20'}`}
    >
      <motion.div
        animate={{ x: enabled ? 20 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md"
      />
    </button>
  );
}

function AutoSpeakReplyToggle() {
  const { autoSpeakReply, setAutoSpeakReply } = useApp();
  const toggle = () => setAutoSpeakReply(!autoSpeakReply);

  return (
    <button
      onClick={toggle}
      className={`w-11 h-6 rounded-full transition-all relative ${autoSpeakReply ? 'bg-celestial-saturn' : 'bg-white/10 border border-white/20'}`}
    >
      <motion.div
        animate={{ x: autoSpeakReply ? 20 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md"
      />
    </button>
  );
}

function WakeWordToggle() {
  const storageKey = 'xf_wake_word_enabled';
  const [enabled, setEnabled] = useState(() => localStorage.getItem(storageKey) === 'true');

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(storageKey, String(next));
    window.dispatchEvent(new CustomEvent('xf:setting-changed', {
      detail: { key: storageKey, value: next },
    }));
  };

  return (
    <button
      onClick={toggle}
      className={`w-11 h-6 rounded-full transition-all relative ${enabled ? 'bg-celestial-saturn' : 'bg-white/10 border border-white/20'}`}
    >
      <motion.div
        animate={{ x: enabled ? 20 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md"
      />
    </button>
  );
}

function AlwaysOnVoiceToggle() {
  const storageKey = 'xf_always_on_voice';
  const [enabled, setEnabled] = useState(() => localStorage.getItem(storageKey) === 'true');

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem(storageKey, String(next));
  };

  return (
    <button
      onClick={toggle}
      className={`w-11 h-6 rounded-full transition-all relative ${enabled ? 'bg-celestial-saturn' : 'bg-white/10 border border-white/20'}`}
    >
      <motion.div
        animate={{ x: enabled ? 20 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-md"
      />
    </button>
  );
}

function CustomWakeWordsEditor({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [words, setWords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);

  // Load from server on mount
  useEffect(() => {
    apiFetch('/api/voice/wake-words', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setWords(d.words || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async (newWords: string[]) => {
    try {
      const res = await apiFetch('/api/voice/wake-words', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: newWords }),
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed' }));
        toast.error(err.error || '保存失败');
        return false;
      }
      const data = await res.json();
      setWords(data.words);
      return true;
    } catch {
      toast.error('保存失败');
      return false;
    }
  };

  const addWord = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (words.length >= 20) {
      toast.error(t.wakeWordMaxReached || ui('最多 20 个自定义唤醒词', 'Maximum 20 custom wake words'));
      return;
    }
    if (trimmed.length > 20) {
      toast.error(ui('每个词最多 20 个字符', 'Max 20 characters per word'));
      return;
    }
    if (words.includes(trimmed)) {
      setInput('');
      return;
    }
    const success = await save([...words, trimmed]);
    if (success) setInput('');
  };

  const removeWord = async (index: number) => {
    const next = [...words];
    next.splice(index, 1);
    await save(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWord();
    }
  };

  return (
    <div className="mt-4 p-4 bg-white/[0.03] rounded-xl border border-white/10 space-y-3">
      <div>
        <p className="text-xs font-bold text-white/80">{t.customWakeWords || ui('自定义唤醒词', 'Custom Wake Words')}</p>
        <p className="text-xs text-white/45 mt-0.5">{t.customWakeWordsDesc || ui('添加额外唤醒语音助手的词。', 'Add words that will also trigger voice assistant.')}</p>
      </div>

      {/* Tags */}
      {loading ? (
        <div className="flex items-center gap-2 text-white/30 text-xs">
          <Loader2 size={12} className="animate-spin" />
          {ui('加载中...', 'Loading...')}
        </div>
      ) : words.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {words.map((w, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-celestial-saturn/15 text-celestial-saturn rounded-full border border-celestial-saturn/20 group">
              {w}
              <button
                onClick={() => removeWord(i)}
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-white/25 italic">{ui('还没有自定义唤醒词', 'No custom wake words yet')}</p>
      )}

      {/* Add input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.wakeWordPlaceholder || ui('输入词语后点击添加...', 'Type a word and press Add...')}
          maxLength={20}
          disabled={words.length >= 20}
          className="flex-1 px-3 py-1.5 text-xs bg-white/[0.06] border border-white/10 rounded-lg text-white/80 placeholder:text-white/25 focus:outline-none focus:border-white/20 disabled:opacity-30"
        />
        <Button
          onClick={addWord}
          disabled={!input.trim() || words.length >= 20}
          className="px-3 py-1 h-auto text-xs bg-white/10 hover:bg-white/15 text-white/80 rounded-lg disabled:opacity-20"
        >
          {t.addWakeWord || ui('添加', 'Add')}
        </Button>
      </div>
    </div>
  );
}

function VisionModelPage({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const { visionConfig, updateVisionConfig } = useApp();
  return (
    <div className="space-y-8">
      <SettingsSection title={t.visionModelSettings || ui('视觉模型', 'Vision Model')} icon={<Camera size={18} className="text-cyan-300" />}>
        <div className="mb-6 rounded-2xl border border-cyan-300/15 bg-cyan-300/[0.04] p-4 space-y-2">
          <label className="text-xs font-black uppercase text-white/55 ml-1">{t.primaryVisionModel || ui('屏幕理解与视觉控制模型', 'Screen Understanding & Vision Control')}</label>
          <CustomSelect
            value={visionConfig.provider}
            options={[
              { value: 'openai', label: 'OpenAI Vision' },
              { value: 'gemini', label: 'Google Gemini Vision' },
              { value: 'ark', label: 'Doubao / 豆包 Vision (Ark)' },
              { value: 'qwen', label: 'Qwen-VL (DashScope)' },
              { value: 'relay', label: 'OpenAI-Compatible Vision' },
            ]}
            onChange={(value) => updateVisionConfig({ provider: value })}
          />
          <p className="text-[12px] text-white/45 px-1">
            {ui('视觉模型只用于看屏幕、OCR、电脑控制等视觉任务；不会改变主推理大脑，也不会改变语音服务。', 'Vision is used only for screen reading, OCR, and desktop control. It does not change the main reasoning brain or voice services.')}
            <span className="ml-2 text-white/40 font-mono">{visionConfig.provider}/{visionConfig.model}</span>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <VisionProviderRow icon={<MessagesSquare size={18} className="text-green-400" />} label="OpenAI Vision" providerId="openai" models={['gpt-4o', 'gpt-4o-mini']} placeholder="sk-..." serverKey="OPENAI_API_KEY" t={t} />
          <VisionProviderRow icon={<BrainCircuit size={18} className="text-blue-400" />} label="Google Gemini Vision" providerId="gemini" models={['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash']} placeholder={ui('输入 Gemini API Key...', 'Enter Gemini API key...')} serverKey="GEMINI_API_KEY" t={t} />
          <VisionProviderRow icon={<Cloud size={18} className="text-cyan-400" />} label="Doubao / 豆包 Vision (Ark)" providerId="ark" models={['doubao-1-5-vision-pro-32k']} placeholder={ui('输入 Ark API Key...', 'Enter Ark API key...')} serverKey="ARK_API_KEY" t={t} />
          <VisionProviderRow icon={<Zap size={18} className="text-violet-400" />} label="Qwen-VL / DashScope" providerId="qwen" models={['qwen-vl-max-latest', 'qwen-vl-max', 'qwen-vl-plus-latest', 'qwen-vl-plus', 'qwen2.5-vl-72b-instruct', 'qwen2.5-vl-7b-instruct', 'qwen2.5-vl-3b-instruct']} placeholder="sk-..." serverKey="DASHSCOPE_API_KEY" t={t} />

          <VisionRelayProviderRow t={t} />
        </div>
      </SettingsSection>
    </div>
  );
}

function LLMProvidersPage({ t, providerStatus }: { t: any; providerStatus: Record<string, { available: boolean; model: string }> }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const { aiConfig, updateAIConfig } = useApp();
  return (
    <div className="space-y-8">
      <SettingsSection title={t.llmProviders || ui('LLM 服务商', 'LLM Providers')} icon={<BrainCircuit size={18} className="text-celestial-saturn" />}>
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-2">
          <label className="text-xs font-black uppercase text-white/55 ml-1">{t.primaryReasoningBrain || ui('主推理大脑', 'Primary Reasoning Brain')}</label>
          <CustomSelect
            value={aiConfig.provider}
            options={[
              { value: 'deepseek', label: 'DeepSeek' },
              { value: 'qwen', label: 'Qwen (DashScope)' },
              { value: 'gemini', label: 'Google Gemini' },
              { value: 'openai', label: 'OpenAI' },
              { value: 'anthropic', label: 'Anthropic Claude' },
              { value: 'ark', label: 'Doubao / 豆包 (Ark)' },
              { value: 'xiaomi', label: 'Xiaomi / 小米' },
              { value: 'kimi', label: 'Kimi / 月之暗面' },
              { value: 'glm', label: 'GLM / 智谱' },
              { value: 'relay', label: '中转站 (API Relay)' },
            ]}
            onChange={(value) => updateAIConfig({ provider: value })}
          />
          <p className="text-[12px] text-white/45 px-1">{t?.activeModel || ui('当前模型', 'Active model')}: <span className="text-white/40 font-mono">{aiConfig.model}</span> - {t?.changePerProvider || ui('可在下方服务商卡片中调整模型。', 'Adjust the model in the provider cards below.')}</p>
        </div>
        <p className="text-sm text-white/40 max-w-xl mb-6">
          {t.apiMatrixLLMDesc || ui('为每个 LLM 服务商配置 API Key 和偏好模型。', 'Configure API keys and preferred models for each LLM provider.')}
        </p>
        <div className="grid grid-cols-1 gap-6">
          <LLMProviderRow icon={<BrainCircuit size={18} className="text-blue-400" />} label="DeepSeek" providerId="deepseek" models={['deepseek-v4-pro', 'deepseek-v4-flash', 'deepseek-chat', 'deepseek-reasoner', 'deepseek-r1-0528']} placeholder="sk-..." serverKey="DEEPSEEK_API_KEY" t={t} />
          <LLMProviderRow icon={<Zap size={18} className="text-violet-400" />} label="Qwen / DashScope (Alibaba Cloud)" providerId="qwen" models={['qwen3.8-max-preview', 'qwen3.7-max', 'qwen3.7-plus', 'qwen3.6-max-preview', 'qwen3.6-plus', 'qwen3.6-flash', 'qwen3.5-max', 'qwen3.5-plus', 'qwen3.5-flash', 'qwen3-coder-plus', 'qwen3-coder-flash', 'qwen-long', 'qwen-plus', 'qwen-turbo', 'qwq-plus', 'qwq-32b', 'qwen-vl-max', 'qwen-vl-plus']} placeholder="sk-..." serverKey="DASHSCOPE_API_KEY" t={t} />
          <LLMProviderRow icon={<Cloud size={18} className="text-cyan-400" />} label="Doubao / 豆包 (Ark)" providerId="ark" models={['doubao-pro-1-5-20250515', 'doubao-lite-1-5-20250515', 'doubao-1-5-pro-32k', 'doubao-1-5-lite-32k', 'doubao-1-5-vision-pro-32k', 'doubao-seed-1-6-20160207', 'doubao-seed-1-6-image-20250410']} placeholder={ui('输入 Ark API Key...', 'Enter Ark API key...')} serverKey="ARK_API_KEY" t={t} />
          <LLMProviderRow icon={<Cpu size={18} className="text-orange-400" />} label="Xiaomi / 小米" providerId="xiaomi" models={['mimo-v2.5-pro', 'mimo-v2', 'xiaomi-chat']} placeholder={ui('输入小米 API Key...', 'Enter Xiaomi API key...')} serverKey="XIAOMI_API_KEY" t={t} />
          <LLMProviderRow icon={<Sparkle size={18} className="text-rose-400" />} label="Kimi / 月之暗面 (Moonshot)" providerId="kimi" models={['kimi-k2.7-code', 'kimi-k2.5', 'kimi-k2.5-thinking', 'moonshot-v1-auto', 'moonshot-v1-8k', 'moonshot-v1-32k', 'moonshot-v1-128k']} placeholder="sk-..." serverKey="KIMI_API_KEY" t={t} />
          <LLMProviderRow icon={<Sparkle size={18} className="text-cyan-400" />} label="GLM / 智谱 (Zhipu AI)" providerId="glm" models={['glm-5.1', 'glm-5', 'glm-4.7', 'glm-4.6', 'glm-4.5', 'glm-4-plus', 'glm-4-flash', 'glm-4-air', 'glm-4-long']} placeholder={ui('输入 GLM API Key...', 'Enter GLM API key...')} serverKey="GLM_API_KEY" t={t} />
          <LLMProviderRow icon={<BrainCircuit size={18} className="text-blue-400" />} label={`Google Gemini${providerStatus.gemini?.available ? ` (${providerStatus.gemini.model})` : ''}`} providerId="gemini" models={['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-pro-preview-05-06', 'gemini-2.0-flash', 'gemini-2.0-flash-lite', 'gemini-1.5-pro', 'gemini-1.5-flash']} placeholder={providerStatus.gemini?.available ? (t.connectedViaEnv || ui('已通过环境变量连接', 'Connected via environment')) : (t.noKeyConfigured || ui('未配置密钥', 'No key configured'))} serverKey="GEMINI_API_KEY" t={t} />
          <LLMProviderRow icon={<MessagesSquare size={18} className="text-green-400" />} label="OpenAI" providerId="openai" models={['o4-mini', 'o3-mini', 'o3-mini-high', 'gpt-4.1', 'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'chatgpt-4o-latest']} placeholder="sk-..." serverKey="OPENAI_API_KEY" t={t} />
          <LLMProviderRow icon={<Sparkle size={18} className="text-purple-400" />} label="Anthropic Claude" providerId="anthropic" models={['claude-opus-4-6', 'claude-opus-4-7', 'claude-sonnet-4-6', 'claude-sonnet-4-5', 'claude-haiku-4-5', 'claude-3-5-sonnet']} placeholder="sk-ant-..." serverKey="ANTHROPIC_API_KEY" t={t} />
          <RelayProviderRow t={t} />
        </div>
      </SettingsSection>
    </div>
  );
}

function SttMonitorCard({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/voice/stt-stats');
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setError(null);
      } else {
        setError('服务暂不可用');
      }
    } catch {
      setError('获取统计失败');
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const iv = setInterval(fetchStats, 5000);
    return () => clearInterval(iv);
  }, [fetchStats]);

  const handleReset = async () => {
    try {
      await apiFetch('/api/voice/stt-stats/reset', { method: 'POST' });
      fetchStats();
    } catch {
      // silent
    }
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return '0s';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return `${m}m ${rs}s`;
  };

  return (
    <div className="mt-6 p-4 bg-white/[0.06] rounded-xl border border-white/10 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs font-bold text-white/80">
            {t.sttMonitorTitle || ui('STT 用量监控', 'STT Usage Monitor')}
          </span>
        </div>
        <button onClick={handleReset} className="text-[11px] text-white/40 hover:text-white/60 transition-colors">
          {t.resetStats || ui('重置', 'Reset')}
        </button>
      </div>

      {error ? (
        <p className="text-xs text-red-400">{error}</p>
      ) : stats ? (
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-black/20 rounded-lg">
            <div className="text-lg font-mono font-bold text-white">{stats.activeSessions}</div>
            <div className="text-[10px] text-white/45">{t.sttActiveSessions || ui('活跃会话', 'Active')}</div>
          </div>
          <div className="text-center p-2 bg-black/20 rounded-lg">
            <div className="text-lg font-mono font-bold text-white">{stats.totalSessions}</div>
            <div className="text-[10px] text-white/45">{t.sttTotalSessions || ui('总会话', 'Total')}</div>
          </div>
          <div className="text-center p-2 bg-black/20 rounded-lg">
            <div className="text-lg font-mono font-bold text-white">{formatDuration(stats.totalMs)}</div>
            <div className="text-[10px] text-white/45">{t.sttDuration || ui('累计时长', 'Duration')}</div>
          </div>
          <div className="text-center p-2 bg-black/20 rounded-lg">
            <div className="text-lg font-mono font-bold text-amber-400">{stats.estimatedCostCny}</div>
            <div className="text-[10px] text-white/45">{t.sttEstimatedCost || ui('预估费用', 'Est. Cost')}</div>
          </div>
        </div>
      ) : (
        <p className="text-xs text-white/40">{t.loading || '加载中...'}</p>
      )}

      {stats && stats.recentEnded?.length > 0 && (
        <div className="pt-2 border-t border-white/[0.08]">
          <p className="text-[10px] text-white/40 mb-1">{t.sttRecentSessions || ui('最近会话', 'Recent Sessions')}</p>
          {stats.recentEnded.slice(0, 3).map((s: any, i: number) => (
            <div key={i} className="flex justify-between text-[10px] text-white/50">
              <span>{s.timeAgo}</span>
              <span className="font-mono">{s.durationS}s</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VoiceServicesPage({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  return (
    <div className="space-y-8">
      <SettingsSection title={t.audioOutput || ui('音频与语音输出', 'Audio & Voice Output')} icon={<Music size={18} className="text-celestial-saturn" />}>
        <div className="space-y-4 mb-6">
          <div className="p-4 rounded-2xl bg-white/[0.06] border border-white/[0.08] space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-bold text-white/80">{t.ttsEngine || ui('TTS 引擎', 'TTS Engine')}</span>
            </div>
            <p className="text-xs text-white/40">{t.ttsEngineDesc || ui('已配置 GPT-SoVITS + DashScope CosyVoice。', 'GPT-SoVITS + DashScope CosyVoice configured.')}</p>
          </div>
          <div className="p-4 rounded-2xl bg-white/[0.06] border border-white/[0.08] space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-bold text-white/80">{t.sttEngine || ui('STT 引擎', 'STT Engine')}</span>
            </div>
            <p className="text-xs text-white/40">{t.sttEngineDesc || ui('Deepgram 语音识别已启用。', 'Deepgram speech recognition active.')}</p>
          </div>
          <VoiceProviderSwitch t={t} />
          <CustomWakeWordsEditor t={t} />
        </div>
        <p className="text-sm text-white/40 max-w-xl mb-6">
          {t.voiceServicesDesc || ui('语音识别（ASR）和语音合成（TTS）。配置豆包语音后会自动优先使用。', 'Speech recognition (ASR) and speech synthesis (TTS). Doubao Speech is auto-prioritized when configured.')}
        </p>
        <div className="grid grid-cols-1 gap-6">
          <ApiKeyField icon={<Volume2 size={18} className="text-emerald-400" />} label={t.doubaoSpeechLabel || '豆包语音（火山引擎 Doubao · STT + TTS）'} placeholder="AppID:AccessToken" storageKey="xf_doubao_speech" serverKey="DOUBAO_SPEECH_KEY" hint={t.doubaoSpeechHint || ui('格式：AppID:AccessToken。可在 console.volcengine.com/speech 的应用管理中获取。', 'Format: AppID:AccessToken. Get both from console.volcengine.com/speech → App Management')} t={t} />
          <ApiKeyField icon={<Zap size={18} className="text-violet-400" />} label={t.dashscopeLabel || '通义千问 / 阿里云百炼（DashScope · Qwen ASR + CosyVoice TTS）'} placeholder="sk-..." storageKey="xf_dashscope_key" serverKey="DASHSCOPE_API_KEY" hint={t.dashscopeHint || ui('用于 Qwen 语音识别(ASR) 与 CosyVoice 语音合成(TTS)。DashScope 即阿里云百炼平台，密钥为 sk- 开头。可在 dashscope.aliyun.com 获取。', 'Powers Qwen ASR and CosyVoice TTS. DashScope is Alibaba Cloud Bailian platform; key starts with sk-. Get your key at dashscope.aliyun.com')} t={t} />
        </div>
        <p className="text-[12px] text-white/45 leading-relaxed flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-celestial-saturn/10 border border-celestial-saturn/20 text-celestial-saturn font-bold">提示</span>
          {t.voiceKeyEitherOr || '豆包语音 与 通义千问/DashScope 是两套备选方案，二选一配置即可；两者都配置时 TTS 优先使用豆包。'}
        </p>
        <div className="mt-6 p-4 bg-white/[0.06] rounded-xl border border-white/10">
          <div className="flex items-center justify-between mt-3">
            <div>
              <p className="text-xs font-bold text-white/80">{t.autoSpeakReply || '自动语音回复'}</p>
              <p className="text-xs text-white/55 mt-0.5">{t.autoSpeakReplyDesc || '开启后，小枫回复时会自动朗读；关闭则仅显示文字，不发声。'}</p>
            </div>
            <AutoSpeakReplyToggle />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-white/80">{t.proactiveVoiceGreeting || '允许小枫主动语音问候'}</p>
              <p className="text-xs text-white/55 mt-0.5">{t.proactiveVoiceGreetingDesc || '开启后，小枫会在检测到异常或长时间不活动时主动开口说话'}</p>
            </div>
            <ProactiveVoiceToggle />
          </div>
          <div className="flex items-center justify-between mt-3">
            <div>
              <p className="text-xs font-bold text-white/80">{t.wakeWordLabel || '唤醒词检测 (Wake Word)'}</p>
              <p className="text-xs text-white/55 mt-0.5">{t.wakeWordDesc || '持续监听"小枫"唤醒词。开启后麦克风持续上传音频做ASR识别，会产生费用。'}</p>
            </div>
            <WakeWordToggle />
          </div>
          <div className="flex items-center justify-between mt-3">
            <div>
              <p className="text-xs font-bold text-white/80">{t.alwaysOnVoiceLabel || '持续语音通道 (Always-On Voice)'}</p>
              <p className="text-xs text-white/55 mt-0.5">{t.alwaysOnVoiceDesc || '开启后麦克风不会自动断开，小枫始终在听。'}</p>
            </div>
            <AlwaysOnVoiceToggle />
          </div>
        </div>
        <SttMonitorCard t={t} />
      </SettingsSection>
    </div>
  );
}

function ApiKeyField({ icon, label, placeholder, disabled = false, storageKey, serverKey, hint, t }: { icon: React.ReactNode, label: string, placeholder: string, disabled?: boolean, storageKey: string, serverKey?: string, hint?: string, t?: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [value, setValue] = useState(() => {
    try { return localStorage.getItem(storageKey) || ''; } catch { return ''; }
  });
  const [saved, setSaved] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [show, setShow] = useState(false);

  const maskKey = (k: string): string => {
    if (!k) return '';
    if (k.length <= 8) return k.slice(0, 2) + '••••' + k.slice(-2);
    return k.slice(0, 4) + '••••••' + k.slice(-4);
  };

  useEffect(() => {
    if (!serverKey) return;
    getSavedKeyStatus()
      .then(data => setServerConfigured(!!data[serverKey]))
      .catch(() => {});
  }, [serverKey]);

  const [confirmRemove, setConfirmRemove] = useState(false);
  const handleRemove = () => {
    if (!confirmRemove) { setConfirmRemove(true); setTimeout(() => setConfirmRemove(false), 3000); return; }
    setConfirmRemove(false);
    const removeLocal = () => {
      localStorage.removeItem(storageKey);
      setServerConfigured(false);
      setValue('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeyRemoved || ui('API Key 已移除', 'API key removed'));
    };
    if (serverKey) {
      saveServerKeys({ [serverKey]: '' })
        .then(removeLocal)
        .catch(err => toast.error(((err) as any).message || t?.failedToRemoveKey || ui('密钥移除失败', 'Failed to remove key')));
      return;
    }
    removeLocal();
  };

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      toast.success(t?.keyCopied || ui('已复制到剪贴板', 'Copied to clipboard'));
    }).catch(() => {});
  };

  const handleSave = () => {
    if (!value.trim()) return;
    const saveLocal = () => {
      localStorage.setItem(storageKey, value.trim());
      setServerConfigured(!!serverKey || serverConfigured);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeySaved || ui('API Key 已保存', 'API key saved'));
    };
    if (serverKey) {
      saveServerKeys({ [serverKey]: value.trim() })
        .then(saveLocal)
        .catch(err => toast.error(((err) as any).message || t?.failedToSaveKey || ui('密钥保存到服务器失败', 'Failed to save key to server')));
      return;
    }
    saveLocal();
  };

  return (
    <div className="p-6 bg-white/[0.06] rounded-3xl border border-white/[0.08] space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-white/[0.06] rounded-lg">{icon}</div>
        <label className="text-xs font-black uppercase tracking-widest text-white/50">{label}</label>
        {serverConfigured && <span className="text-xs px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full font-bold">{t?.configured || ui('已配置', 'CONFIGURED')}</span>}
        {saved && <CheckCircle size={14} className="text-green-400 ml-auto" />}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            disabled={disabled}
            type={show ? 'text' : 'password'}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder={serverConfigured && !value ? (t?.keySavedOnServer || ui('密钥已保存在服务器（输入可替换）', 'Key saved on server (type to replace)')) : placeholder}
            className="w-full bg-black/40 border border-white/[0.10] rounded-xl p-4 pr-36 text-white font-mono text-sm outline-none focus:border-celestial-saturn/50 transition-colors disabled:opacity-50"
          />
          {/* 复制按钮 */}
          <button
            type="button"
            onClick={handleCopy}
            disabled={disabled || !value}
            className="absolute right-28 top-2 h-10 w-10 flex items-center justify-center bg-white/[0.06] border border-white/[0.10] rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.10] disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            title={t?.copyKey || ui('复制', 'Copy')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
          {/* 显示/隐藏 */}
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            disabled={disabled || !value}
            title={show ? (t?.hideKey || ui('隐藏', 'Hide')) : (t?.showKey || ui('显示', 'Show'))}
            className="absolute right-[4.5rem] top-2 h-10 w-10 flex items-center justify-center bg-white/[0.06] border border-white/[0.10] rounded-lg text-white/60 hover:bg-white/[0.10] hover:text-white/90 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          {/* 移除 */}
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled || (!value && !serverConfigured)}
            className="absolute right-2 top-2 h-10 px-3 bg-red-500/10 border border-red-500/20 rounded-lg text-[11px] font-bold uppercase tracking-tight text-red-400 hover:bg-red-500/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
          >
            {confirmRemove ? (t?.confirmRemove || ui('确认移除', 'Confirm')) : (t?.remove || ui('移除', 'Remove'))}
          </button>
        </div>
        <Button
          onClick={handleSave}
          disabled={disabled || !value.trim()}
          className="h-[56px] px-6 bg-celestial-saturn text-black rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed hover:bg-celestial-saturn/90 transition-all"
        >
          {t?.save || ui('保存', 'Save')}
        </Button>
      </div>
      {serverConfigured && value && (
        <p className="text-[12px] text-white/45 leading-relaxed font-mono">
          {t?.keyMaskedPreview || ui('已保存（脱敏预览）：', 'Saved (masked): ')}{maskKey(value)}
        </p>
      )}
      {hint && <p className="text-[12px] text-white/45 leading-relaxed">{hint}</p>}
    </div>
  );
}


function RelayProviderRow({ t }: { t?: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [apiKey, setApiKey] = useState(() => {
    try { return localStorage.getItem('xf_relay_key') || ''; } catch { return ''; }
  });
  const [baseUrl, setBaseUrl] = useState(() => {
    try { return localStorage.getItem('xf_relay_url') || 'https://api.example.com/v1'; } catch { return 'https://api.example.com/v1'; }
  });
  const [serverKeyOk, setServerKeyOk] = useState(false);
  const [serverUrlOk, setServerUrlOk] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSavedKeyStatus()
      .then(data => {
        setServerKeyOk(!!data['RELAY_API_KEY']);
        setServerUrlOk(!!data['RELAY_BASE_URL']);
      })
      .catch(() => {});
  }, []);

  const handleSave = () => {
    if (!apiKey.trim() || !baseUrl.trim()) return;
    saveServerKeys({ RELAY_API_KEY: apiKey.trim(), RELAY_BASE_URL: baseUrl.trim() }).then(() => {
      localStorage.setItem('xf_relay_key', apiKey.trim());
      localStorage.setItem('xf_relay_url', baseUrl.trim());
      setServerKeyOk(true);
      setServerUrlOk(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeySaved || ui('API Key 已保存', 'API key saved'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToSaveKey || ui('保存失败', 'Failed to save')));
  };

  const handleRemove = () => {
    saveServerKeys({ RELAY_API_KEY: '', RELAY_BASE_URL: '' }).then(() => {
      localStorage.removeItem('xf_relay_key');
      localStorage.removeItem('xf_relay_url');
      setServerKeyOk(false);
      setServerUrlOk(false);
      setApiKey('');
      setBaseUrl('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t?.apiKeyRemoved || ui('API Key 已移除', 'API key removed'));
    }).catch(err => toast.error(((err) as any).message || t?.failedToRemoveKey || ui('移除失败', 'Failed to remove')));
  };

  return (
    <div className="p-6 bg-white/[0.06] rounded-3xl border border-white/[0.08] space-y-4">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-white/[0.06] rounded-lg"><Globe size={18} className="text-cyan-400" /></div>
        <label className="text-xs font-black uppercase tracking-widest text-white/50">中转站 (API Relay)</label>
        {(serverKeyOk || serverUrlOk) && <span className="text-xs px-2 py-0.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full font-bold">{ui('已配置', 'CONFIGURED')}</span>}
        {saved && <CheckCircle size={14} className="text-green-400 ml-auto" />}
      </div>
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="API Key"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl p-4 text-white font-mono text-sm outline-none focus:border-cyan-400/50 transition-colors"
          />
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="https://your-relay.example.com/v1"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl p-4 text-white font-mono text-sm outline-none focus:border-cyan-400/50 transition-colors"
          />
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          disabled={!apiKey.trim() || !baseUrl.trim()}
          className="h-[48px] px-6 bg-cyan-600 text-white rounded-xl text-xs font-black uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cyan-500 transition-all"
        >
          {t?.save || ui('保存', 'Save')}
        </Button>
        <Button
          onClick={handleRemove}
          disabled={!apiKey && !serverKeyOk && !serverUrlOk}
          className="h-[48px] px-4 bg-red-500/10 border border-red-500/20 rounded-xl text-xs font-black uppercase tracking-widest text-red-400 hover:bg-red-500/20 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        >
          {t?.remove || ui('移除', 'Remove')}
        </Button>
      </div>
      <p className="text-[12px] text-white/45 leading-relaxed">{ui('兼容 OpenAI 的 API 中转服务。请输入代理/中转服务的 Base URL 和 API Key。', 'OpenAI-compatible API relay. Enter the base URL and API key of your proxy/relay service.')}</p>
    </div>
  );
}

type AutonomyGateConfig = {
  alwaysOnline: boolean;
  autoProcessEnabled: boolean;
  externalAppAutomationEnabled: boolean;
  messagingSendRequiresConfirmation: boolean;
  maxConsecutiveTasks: number;
  allowedHours: { start: number; end: number }[];
  requireIdle: boolean;
  minIdleSeconds: number;
  maxTokensPerHour: number;
  quietHoursEnabled?: boolean;
  quietHoursStart?: number;
  quietHoursEnd?: number;
};

type NativeRuntimeStatus = {
  platform: string;
  autostart_supported: boolean;
  autostart_enabled: boolean;
  autostart_entry: string;
  close_to_background: boolean;
  started_in_background: boolean;
  backend_node_running: boolean;
  backend_python_running: boolean;
  node_restarts: number;
  python_restarts: number;
  global_shortcut: string;
  notes: string[];
};

const DEFAULT_AUTONOMY_GATE: AutonomyGateConfig = {
  alwaysOnline: true,
  autoProcessEnabled: false,
  externalAppAutomationEnabled: false,
  messagingSendRequiresConfirmation: true,
  maxConsecutiveTasks: 1,
  allowedHours: [{ start: 8, end: 22 }],
  requireIdle: true,
  minIdleSeconds: 120,
  maxTokensPerHour: 3000,
};

function AutonomousSettingsPanel({ t, operationMode, setOperationMode }: { t: any; operationMode: OperationMode; setOperationMode: (m: OperationMode) => void }) {
  const [gateConfig, setGateConfig] = useState<AutonomyGateConfig>(DEFAULT_AUTONOMY_GATE);
  const [nativeRuntime, setNativeRuntime] = useState<NativeRuntimeStatus | null>(null);
  const [nativeRuntimeError, setNativeRuntimeError] = useState('');
  const [taskList, setTaskList] = useState<any[]>([]);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  useEffect(() => {
    apiFetch('/api/autonomy/gate_config')
      .then(r => r.json())
      .then(d => setGateConfig({ ...DEFAULT_AUTONOMY_GATE, ...(d || {}) }))
      .catch(() => {});
    apiFetch('/api/scheduler/tasks')
      .then(r => r.json())
      .then(d => setTaskList(d.tasks || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const loadNativeRuntime = async () => {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        const status = await invoke<NativeRuntimeStatus>('get_runtime_resilience_status');
        if (!cancelled) {
          setNativeRuntime(status);
          setNativeRuntimeError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setNativeRuntime(null);
          setNativeRuntimeError(err?.message || ui('原生常驻运行控制仅在桌面客户端可用。', 'Native runtime controls are only available in the desktop client.'));
        }
      }
    };
    void loadNativeRuntime();
    timer = window.setInterval(loadNativeRuntime, 30000);
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, []);

  const updateGate = (partial: Partial<AutonomyGateConfig>) => {
    const updated = { ...gateConfig, ...partial };
    setGateConfig(updated);
    apiFetch('/api/autonomy/gate_config', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    })
      .then(async r => {
        const data = await r.json().catch(() => null);
        if (!r.ok) throw new Error(data?.error || ui('自主设置更新失败', 'Failed to update autonomy settings'));
        if (data) setGateConfig({ ...DEFAULT_AUTONOMY_GATE, ...data });
      })
      .catch((err: any) => {
        setGateConfig(gateConfig);
        toast.error(err?.message || ui('自主设置更新失败', 'Failed to update autonomy settings'));
      });
  };

  const refreshNativeRuntime = async () => {
    const { invoke } = await import('@tauri-apps/api/core');
    const status = await invoke<NativeRuntimeStatus>('get_runtime_resilience_status');
    setNativeRuntime(status);
    setNativeRuntimeError('');
    return status;
  };

  const updateNativeRuntime = async (kind: 'autostart' | 'closeToBackground', enabled: boolean) => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      if (kind === 'autostart') {
        await invoke('set_autostart_enabled', { enabled });
      } else {
        localStorage.setItem('xf_close_to_background', String(enabled));
        await invoke('set_close_to_background', { enabled });
      }
      await refreshNativeRuntime();
      toast.success(enabled ? ui('已开启', 'Enabled') : ui('已关闭', 'Disabled'));
    } catch (err: any) {
      toast.error(err?.message || ui('原生运行设置更新失败', 'Failed to update native runtime setting'));
    }
  };

  const invokeRuntimeAction = async (action: 'hide' | 'quit') => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke(action === 'hide' ? 'hide_to_background' : 'quit_app');
    } catch (err: any) {
      toast.error(err?.message || ui('运行时操作失败', 'Runtime action failed'));
    }
  };

  const toggleTask = async (taskId: string) => {
    try {
      const r = await apiFetch(`/api/scheduler/tasks/${taskId}/toggle`, { method: 'POST', credentials: 'include' });
      const data = await r.json();
      setTaskList(prev => prev.map(t => t.id === taskId ? { ...t, enabled: data.enabled } : t));
    } catch {}
  };

  const isAutonomous = operationMode === 'autonomous';
  const isAllDay = gateConfig.allowedHours?.length === 1 && gateConfig.allowedHours[0]?.start === 0 && gateConfig.allowedHours[0]?.end === 24;
  const ToggleRow = ({
    label,
    desc,
    checked,
    onClick,
    danger,
  }: {
    label: string;
    desc: string;
    checked: boolean;
    onClick: () => void;
    danger?: boolean;
  }) => (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-black/18 px-3 py-3">
      <div>
        <div className="text-xs font-bold text-white/72">{label}</div>
        <div className="mt-1 text-[11px] leading-relaxed text-white/36">{desc}</div>
      </div>
      <button
        onClick={onClick}
        className={`h-5 w-10 shrink-0 rounded-full transition-all ${checked ? (danger ? 'bg-amber-400' : 'bg-cyan-500') : 'bg-white/10'}`}
      >
        <div className={`h-3 w-3 rounded-full bg-white transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Operation Mode */}
      <div className="p-4 bg-white/[0.06] rounded-2xl border border-white/[0.08] space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-white/60">{ui('自主模式', 'Autonomy Mode')}</div>
            <p className="text-xs text-white/40 mt-1">{ui('允许 小枫 先给行动指南，再用工具、运行日志、桌面控制和团队智能体处理多步工作。', 'Allow 小枫 to give an action guide, then handle multi-step work with tools, run logs, desktop control, and team agents.')}</p>
          </div>
          <button
            onClick={() => setOperationMode(isAutonomous ? 'assistant' : 'autonomous')}
            className={`w-11 h-6 rounded-full transition-all ${isAutonomous ? 'bg-cyan-500' : 'bg-white/10'}`}
          >
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isAutonomous ? 'translate-x-[24px]' : 'translate-x-[2px]'}`} />
          </button>
        </div>
        {isAutonomous && (
          <div className="flex items-center gap-2 text-xs text-cyan-400/70">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            {ui('自主模式已开启：小枫 可以把复杂任务拆成可见的工作路径。', 'Autonomy active: 小枫 can turn complex tasks into visible work paths.')}
          </div>
        )}
      </div>

      {/* Always Online */}
      <div className="p-4 bg-white/[0.06] rounded-2xl border border-white/[0.08] space-y-3">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-white/60">{ui('常驻在线', 'Always Online')}</div>
          <p className="text-xs text-white/40 mt-1">{ui('桌面客户端或后台服务运行时，小枫 可以保持待命。自动处理由下面的安全门单独控制。', '小枫 can stay ready while the desktop client or background server is running. Automatic processing is controlled separately.')}</p>
        </div>
        <ToggleRow
          label={ui('保持 小枫 待命', 'Keep 小枫 ready')}
          desc={ui('应用在线时允许后台扫描、状态中继、计划任务和待处理工作流检查。', 'Allows background scans, state relay, schedulers, and pending workflow checks while the app is online.')}
          checked={gateConfig.alwaysOnline}
          onClick={() => updateGate({ alwaysOnline: !gateConfig.alwaysOnline })}
        />
        <ToggleRow
          label={ui('自动处理已确认工作流', 'Auto-process confirmed workflows')}
          desc={ui('只有用户已经同意该工作流后，小枫 才会执行排队的后台工作。', 'Lets 小枫 execute queued background work only after the user has agreed to that workflow.')}
          checked={gateConfig.autoProcessEnabled}
          onClick={() => updateGate({ autoProcessEnabled: !gateConfig.autoProcessEnabled })}
          danger
        />
        <ToggleRow
          label={ui('24 小时工作窗口', '24-hour work window')}
          desc={ui('开启后安全门允许任意时间自动工作；空闲、预算和确认限制仍然生效。', 'When enabled, the safety gate permits automatic work at any hour; idle, budget, and confirmation gates still apply.')}
          checked={isAllDay}
          onClick={() => updateGate({ allowedHours: isAllDay ? [{ start: 8, end: 22 }] : [{ start: 0, end: 24 }] })}
        />
        <div className="rounded-xl border border-white/8 bg-black/18 p-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold text-white/72">{ui('原生常驻运行', 'Native resident runtime')}</div>
              <div className="mt-1 text-[11px] leading-relaxed text-white/36">
                {ui('控制已安装的桌面客户端是否随 Windows 启动，以及关闭窗口后是否继续保持后台运行。', 'Controls whether the installed desktop client starts with Windows and stays alive when the window is closed.')}
              </div>
            </div>
            <button
              onClick={() => refreshNativeRuntime().catch((err: any) => toast.error(err?.message || ui('运行时状态刷新失败', 'Runtime refresh failed')))}
              className="shrink-0 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/45 hover:bg-white/[0.08] hover:text-white"
            >
              {ui('刷新', 'Refresh')}
            </button>
          </div>
          {nativeRuntime ? (
            <div className="space-y-2">
              <ToggleRow
                label={ui('开机登录后启动', 'Launch at login')}
                desc={nativeRuntime.autostart_supported ? ui('为当前 Windows 用户安装项以 --background 模式启动 小枫。', 'Starts 小枫 with --background for current Windows user installs.') : ui('当前平台暂不支持登录后启动。', 'Launch at login is not supported on this platform yet.')}
                checked={nativeRuntime.autostart_enabled}
                onClick={() => nativeRuntime.autostart_supported && updateNativeRuntime('autostart', !nativeRuntime.autostart_enabled)}
              />
              <ToggleRow
                label={ui('关闭按钮转入后台', 'Close button hides to background')}
                desc={ui('顶部关闭按钮和 Alt+F4 会隐藏 小枫，而不是停止后端。需要完全退出时使用托盘退出。', 'The top close button and Alt+F4 hide 小枫 instead of stopping the backend. Use tray Quit to fully exit.')}
                checked={nativeRuntime.close_to_background}
                onClick={() => updateNativeRuntime('closeToBackground', !nativeRuntime.close_to_background)}
              />
              <div className="grid grid-cols-2 gap-2 text-[11px] text-white/42">
                <div className="rounded-lg bg-white/[0.03] px-3 py-2">{ui('平台', 'Platform')}: {nativeRuntime.platform}</div>
                <div className="rounded-lg bg-white/[0.03] px-3 py-2">{ui('快捷键', 'Shortcut')}: {nativeRuntime.global_shortcut}</div>
                <div className="rounded-lg bg-white/[0.03] px-3 py-2">{ui('后端', 'Backend')}: {nativeRuntime.backend_node_running ? ui('运行中', 'Running') : ui('开发模式 / 未拉起', 'Dev / not spawned')}</div>
                <div className="rounded-lg bg-white/[0.03] px-3 py-2">{ui('重启次数', 'Restarts')}: {nativeRuntime.node_restarts}</div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  onClick={() => invokeRuntimeAction('hide')}
                  className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white/50 hover:bg-white/[0.08] hover:text-white"
                >
                  {ui('隐藏到后台', 'Hide Now')}
                </button>
                <button
                  onClick={() => invokeRuntimeAction('quit')}
                  className="rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-red-200/70 hover:bg-red-400/15 hover:text-red-100"
                >
                  {ui('退出 小枫', 'Quit 小枫')}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-white/38">
              {nativeRuntimeError || ui('正在加载原生运行状态...', 'Native runtime status is loading...')}
            </div>
          )}
        </div>
      </div>

      {/* External Apps */}
      <div className="p-4 bg-white/[0.06] rounded-2xl border border-white/[0.08] space-y-3">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-white/60">{ui('外部应用', 'External Apps')}</div>
          <p className="text-xs text-white/40 mt-1">{ui('浏览器、微信、CAD 和其他 AI 应用会先经过适配器路由，再由 小枫 使用视觉控制。', 'Browser, WeChat, CAD, and other AI apps are routed through adapters before 小枫 uses visual control.')}</p>
        </div>
        <ToggleRow
          label={ui('允许外部应用自动化', 'Allow external app automation')}
          desc={ui('小枫 通过适配器打开或控制外部应用前需要开启。关闭时只生成草稿和文件。', 'Required before 小枫 opens or controls external apps through adapters. Keep off for draft-only behavior.')}
          checked={gateConfig.externalAppAutomationEnabled}
          onClick={() => updateGate({ externalAppAutomationEnabled: !gateConfig.externalAppAutomationEnabled })}
          danger
        />
        <ToggleRow
          label={ui('发送消息前确认', 'Confirm before message sending')}
          desc={ui('小枫 可以准备并复制消息草稿，但最终发送仍需要用户确认。', '小枫 may prepare and copy message drafts, but sending should remain user-confirmed.')}
          checked={gateConfig.messagingSendRequiresConfirmation}
          onClick={() => updateGate({ messagingSendRequiresConfirmation: !gateConfig.messagingSendRequiresConfirmation })}
        />
      </div>

      {/* Safety Gates */}
      <div className="p-4 bg-white/[0.06] rounded-2xl border border-white/[0.08] space-y-4">
        <div className="text-xs font-black uppercase tracking-widest text-white/60">{ui('安全门', 'Safety Gates')}</div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-white/50">{ui('要求用户空闲', 'Require user idle')}</span>
          <button
            onClick={() => updateGate({ requireIdle: !gateConfig.requireIdle })}
            className={`w-10 h-5 rounded-full transition-all ${gateConfig.requireIdle ? 'bg-cyan-500' : 'bg-white/10'}`}
          >
            <div className={`w-3 h-3 rounded-full bg-white transition-transform ${gateConfig.requireIdle ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-white/50">{ui(`最短空闲时间：${gateConfig.minIdleSeconds} 秒`, `Min idle time: ${gateConfig.minIdleSeconds}s`)}</span>
          </div>
          <input
            type="range" min={30} max={600} step={30} value={gateConfig.minIdleSeconds}
            onChange={e => updateGate({ minIdleSeconds: parseInt(e.target.value) })}
            className="w-full accent-cyan-500"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-white/50">{ui(`最大连续任务数：${gateConfig.maxConsecutiveTasks}`, `Max consecutive tasks: ${gateConfig.maxConsecutiveTasks}`)}</span>
          </div>
          <input
            type="range" min={1} max={10} step={1} value={gateConfig.maxConsecutiveTasks}
            onChange={e => updateGate({ maxConsecutiveTasks: parseInt(e.target.value) })}
            className="w-full accent-cyan-500"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-white/50">{ui(`每小时最大 Token：${gateConfig.maxTokensPerHour}`, `Max tokens/hour: ${gateConfig.maxTokensPerHour}`)}</span>
          </div>
          <input
            type="range" min={500} max={10000} step={500} value={gateConfig.maxTokensPerHour}
            onChange={e => updateGate({ maxTokensPerHour: parseInt(e.target.value) })}
            className="w-full accent-cyan-500"
          />
        </div>
      </div>

      {/* Scheduler Tasks */}
      <div className="p-4 bg-white/[0.06] rounded-2xl border border-white/[0.08] space-y-3">
        <button onClick={() => setTasksExpanded(!tasksExpanded)} className="w-full flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-widest text-white/60">{ui('后台任务', 'Background Tasks')}</div>
          <ChevronDown size={14} className={`text-white/40 transition-transform ${tasksExpanded ? 'rotate-180' : ''}`} />
        </button>

        {tasksExpanded && (
          <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
            {taskList.map((task: any) => (
              <div key={task.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/[0.02]">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-white/60 truncate">{task.id}</div>
                  <div className="text-[11px] text-white/30">{task.cron} {task.lastRun ? ui(`· 上次：${new Date(task.lastRun).toLocaleTimeString()}`, `· Last: ${new Date(task.lastRun).toLocaleTimeString()}`) : ''}</div>
                </div>
                <button
                  onClick={() => toggleTask(task.id)}
                  className={`w-8 h-4 rounded-full transition-all ${task.enabled !== false ? 'bg-cyan-500/50' : 'bg-white/10'}`}
                >
                  <div className={`w-3 h-3 rounded-full bg-white transition-transform ${task.enabled !== false ? 'translate-x-[18px]' : 'translate-x-[1px]'}`} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 个人中心 ──
function ProfileSection({ t }: { t: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const { user } = useApp();
  const u = user as Record<string, any> | null;

  return (
    <div className="space-y-6">
      {/* 用户信息卡 */}
      <div className="p-6 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08]">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-celestial-saturn/40 to-celestial-saturn/10 border-2 border-celestial-saturn/30 flex items-center justify-center text-2xl flex-shrink-0">
            {u?.avatar ? (
              <img src={u.avatar} className="w-full h-full rounded-full object-cover" alt="" />
            ) : (
              <span className="text-white/80 font-black">{String(u?.displayName || u?.username || 'U')[0]?.toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-white/90">{u?.displayName || u?.username || ui('用户', 'User')}</h3>
            <div className="flex items-center gap-2 mt-1">
              {u?.role && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  u.role === 'admin' ? 'bg-celestial-saturn/10 border border-celestial-saturn/30 text-celestial-saturn' :
                  u.role === 'super_admin' ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400' :
                  'bg-white/[0.08] border border-white/[0.10] text-white/50'
                }`}>
                  {u.role === 'super_admin' ? '超级管理员' : u.role === 'admin' ? '管理员' : u.role}
                </span>
              )}
              {u?.uid && <span className="text-[10px] text-white/30 font-mono">UID: {u.uid}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* 账号信息 */}
      <SettingsSection title={ui('账号信息', 'Account Info')} icon={<ShieldCheck size={18} className="text-blue-400" />}>
        <div className="p-6 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08] space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-white/[0.04]">
            <div className="flex items-center gap-2">
              <UserCircle size={14} className="text-white/30" />
              <span className="text-xs text-white/50">{ui('用户ID', 'User ID')}</span>
            </div>
            <span className="text-xs font-mono text-white/70">{u?.uid || '-'}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/[0.04]">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-white/30" />
              <span className="text-xs text-white/50">{ui('邮箱', 'Email')}</span>
            </div>
            <span className="text-xs font-mono text-white/70">{u?.email || '-'}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/[0.04]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-white/30" />
              <span className="text-xs text-white/50">{ui('角色', 'Role')}</span>
            </div>
            <span className="text-xs font-bold text-white/80">{u?.role || ui('用户', 'User')}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-white/30" />
              <span className="text-xs text-white/50">{ui('注册时间', 'Member Since')}</span>
            </div>
            <span className="text-xs text-white/60">
              {u?.createdAt ? new Date(u.createdAt).toLocaleDateString('zh-CN') : '-'}
            </span>
          </div>
        </div>
      </SettingsSection>

      {/* 安全设置 */}
      <SettingsSection title={ui('安全设置', 'Security')} icon={<ShieldCheck size={18} className="text-green-400" />}>
        <div className="p-6 bg-white/[0.06] rounded-[2.5rem] border border-white/[0.08] space-y-3">
          <button className="w-full flex items-center justify-between py-3 px-4 bg-white/[0.04] rounded-2xl border border-white/[0.06] hover:border-white/[0.12] cursor-pointer transition-colors" onClick={() => window.location.href = '/#settings/security'}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/[0.10] border border-blue-500/[0.15] flex items-center justify-center">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-white/80">{ui('修改密码', 'Change Password')}</div>
                <div className="text-[11px] text-white/35">{ui('更新您的登录密码', 'Update your login password')}</div>
              </div>
            </div>
            <ChevronRight size={16} className="text-white/25" />
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

function SettingsSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{title}</h3>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function SettingsItem({ label, desc, active = false, storageKey, onChange, t }: { label: string; desc: string; active?: boolean; storageKey?: string; onChange?: (v: boolean) => void; t?: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [isActive, setIsActive] = useState(() => {
    if (storageKey) {
      try { return localStorage.getItem(storageKey) === 'true'; } catch { return active; }
    }
    return active;
  });

  const toggle = () => {
    const next = !isActive;
    setIsActive(next);
    if (storageKey) {
      const mapped = STORAGE_TO_SETTING[storageKey];
      if (mapped) {
        setUserSetting(mapped, next);
      } else {
        localStorage.setItem(storageKey, String(next));
        window.dispatchEvent(new CustomEvent('xf:setting-changed', { detail: { key: storageKey, value: next } }));
      }
    }
    onChange?.(next);

    // 🔧 v3.12.46: Toast 带撤销按钮（3 秒内可回退）
    const toastId = toast.info(`${label}: ${next ? (t?.enabled || ui('已开启', 'Enabled')) : (t?.disabled || ui('已关闭', 'Disabled'))}`, {
      action: {
        label: ui('撤销', 'Undo'),
        onClick: () => {
          setIsActive(!next);
          if (storageKey) {
            const mapped = STORAGE_TO_SETTING[storageKey];
            if (mapped) setUserSetting(mapped, !next);
            else {
              localStorage.setItem(storageKey, String(!next));
              window.dispatchEvent(new CustomEvent('xf:setting-changed', { detail: { key: storageKey, value: !next } }));
            }
          }
          onChange?.(!next);
          toast.dismiss(toastId);
        },
      },
      duration: 3000,
    });
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white/[0.06] rounded-2xl border border-white/[0.06] hover:border-white/[0.10] transition-colors">
      <div className="space-y-0.5 pr-3 min-w-0">
        <div className="font-bold text-[13px] text-white/90">{label}</div>
        <div className="text-[11px] text-white/35 leading-tight line-clamp-2">{desc}</div>
      </div>
      <button
        onClick={toggle}
        role="switch"
        aria-checked={isActive}
        aria-label={label}
        className={`flex-shrink-0 w-10 h-5 rounded-full p-1 transition-colors cursor-pointer ${isActive ? 'bg-celestial-saturn' : 'bg-white/[0.12]'}`}
      >
        <div className={`w-3 h-3 rounded-full bg-white transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
    </div>
  );
}

/**
 * 已隐藏设备管理器 — 显示黑名单中的设备，支持取消隐藏
 * 读取 localStorage xf_mijia_hidden_devices（JSON 数组），
 * 通过 xf:setting-changed 事件实时同步
 */
function HiddenDevicesManager({ t }: { t?: any }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  const [hiddenIds, setHiddenIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('xf_mijia_hidden_devices');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // 监听外部变化（如面板上隐藏操作触发的事件）
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'xf_mijia_hidden_devices') {
        setHiddenIds(detail.value ?? []);
      }
    };
    window.addEventListener('xf:setting-changed', handler);
    return () => window.removeEventListener('xf:setting-changed', handler);
  }, []);

  const unhide = (id: string) => {
    const updated = hiddenIds.filter(h => h !== id);
    setHiddenIds(updated);
    localStorage.setItem('xf_mijia_hidden_devices', JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('xf:setting-changed', {
      detail: { key: 'xf_mijia_hidden_devices', value: updated },
    }));
  };

  const unhideAll = () => {
    setHiddenIds([]);
    localStorage.setItem('xf_mijia_hidden_devices', JSON.stringify([]));
    window.dispatchEvent(new CustomEvent('xf:setting-changed', {
      detail: { key: 'xf_mijia_hidden_devices', value: [] },
    }));
  };

  if (hiddenIds.length === 0) return null;

  return (
    <div className="mt-3 p-4 bg-white/[0.03] rounded-2xl border border-white/[0.06] space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-widest text-white/35">
          {ui('已隐藏设备', 'Hidden Devices')} ({hiddenIds.length})
        </span>
        <button
          onClick={unhideAll}
          className="text-[10px] text-cyan-400/60 hover:text-cyan-400/90 transition-colors duration-150"
        >
          {ui('全部恢复', 'Restore All')}
        </button>
      </div>
      <div className="space-y-1.5">
        {hiddenIds.map(id => (
          <div key={id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.04]">
            <span className="text-[12px] text-white/55 font-mono">{id}</span>
            <button
              onClick={() => unhide(id)}
              className="text-[10px] px-2 py-1 rounded-lg text-white/35 hover:text-white/70 hover:bg-white/8 transition-all duration-150"
            >
              {ui('恢复显示', 'Restore')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** 更新日志面板 — 时间线风格版本卡片 */
function ChangelogPanel({ t, lang }: { t: any; lang: string }) {
  const isZh = lang !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  // 每个版本的渐变主题色（循环使用）
  const themes = [
    { card: 'from-cyan-500/15 via-blue-500/10 to-sky-500/8 border-cyan-400/20', badge: 'bg-gradient-to-r from-cyan-400/20 to-blue-400/20 text-cyan-300 border border-cyan-400/15', dot: 'from-celestial-mars to-celestial-saturn', dotText: 'text-celestial-mars' },
    { card: 'from-purple-500/15 via-fuchsia-500/10 to-pink-500/8 border-purple-400/20', badge: 'bg-gradient-to-r from-purple-400/20 to-fuchsia-400/20 text-purple-300 border border-purple-400/15', dot: 'from-purple-400 to-pink-400', dotText: 'text-white/30' },
    { card: 'from-emerald-500/15 via-teal-500/10 to-green-500/8 border-emerald-400/20', badge: 'bg-gradient-to-r from-emerald-400/20 to-teal-400/20 text-emerald-300 border border-emerald-400/15', dot: 'from-emerald-400 to-teal-400', dotText: 'text-white/30' },
    { card: 'from-amber-500/15 via-orange-500/10 to-yellow-500/8 border-amber-400/20', badge: 'bg-gradient-to-r from-amber-400/20 to-orange-400/20 text-amber-300 border border-amber-400/15', dot: 'from-amber-400 to-orange-400', dotText: 'text-white/30' },
    { card: 'from-rose-500/15 via-red-500/10 to-pink-500/8 border-rose-400/20', badge: 'bg-gradient-to-r from-rose-400/20 to-red-400/20 text-rose-300 border border-rose-400/15', dot: 'from-rose-400 to-red-400', dotText: 'text-white/30' },
    { card: 'from-indigo-500/15 via-violet-500/10 to-blue-500/8 border-indigo-400/20', badge: 'bg-gradient-to-r from-indigo-400/20 to-violet-400/20 text-indigo-300 border border-indigo-400/15', dot: 'from-indigo-400 to-violet-400', dotText: 'text-white/30' },
  ];

  const totalVersions = CHANGELOG.length;

  return (
    <div className="p-6 space-y-5 h-full overflow-y-auto">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-white/90 tracking-wide">
            ✨ {ui('更新日志', 'Changelog')}
          </h2>
          <p className="text-xs text-white/35 mt-1">
            {ui('记录每个版本的演进历程 · 共', 'Track the evolution of each version · ')}
            <span className="text-cyan-400/70 font-mono">{totalVersions}</span>
            {ui(' 个版本', ' versions')}
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-xl font-mono text-sm font-bold bg-gradient-to-r from-celestial-saturn/25 to-celestial-mars/25 text-celestial-mars border border-celestial-mars/20 shadow-sm shadow-celestial-mars/10`}>
          v{APP_VERSION}
        </div>
      </div>

      {/* 版本时间线 */}
      <div className="relative space-y-4">
        {/* 时间线竖线 — 渐变色 */}
        <div className="absolute left-[19px] top-3 bottom-0 w-[2px] rounded-full bg-gradient-to-b from-cyan-400/40 via-purple-400/25 via-emerald-400/20 via-amber-400/15 to-transparent" />

        {CHANGELOG.map((entry, idx) => {
          const theme = themes[idx % themes.length];
          const isFirst = idx === 0;

          return (
            <motion.div
              key={entry.version}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex gap-4"
            >
              {/* 时间线圆点 */}
              <div className="relative z-10 flex-shrink-0 mt-2.5">
                <div
                  className={`w-[38px] h-[38px] rounded-full flex items-center justify-center border-2 ${
                    isFirst
                      ? `bg-gradient-to-br ${theme.dot} border-white/20 shadow-lg shadow-${theme.dot.includes('mars') ? 'celestial-mars' : 'purple'}-400/25`
                      : `bg-gradient-to-br ${theme.dot} border-white/[0.08]`
                  }`}
                >
                  {isFirst
                    ? <Rocket size={14} className={theme.dotText} />
                    : <Sparkle size={12} className={theme.dotText} />
                  }
                </div>
                {isFirst && (
                  <>
                    <div className="absolute -inset-1 w-[38px] h-[38px] rounded-full bg-celestial-mars/15 animate-ping" style={{ animationDuration: '3s' }} />
                    <div className="absolute -inset-2 w-[46px] h-[46px] rounded-full bg-celestial-mars/8 blur-md" />
                  </>
                )}
              </div>

              {/* 卡片内容 */}
              <div className={`flex-1 rounded-2xl border p-[18px] bg-gradient-to-br backdrop-blur-md hover:bg-white/[0.04] transition-all duration-300 ${theme.card}`}>
                {/* 版本号行 */}
                <div className="flex items-center gap-3 mb-3.5 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-lg font-mono text-xs font-bold ${theme.badge}`}>
                    {entry.version}
                    {isFirst && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-current inline-block animate-pulse" />}
                  </span>
                  <span className="text-xs text-white/35 font-mono">{entry.date}</span>
                  {isFirst && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-emerald-400/20 to-cyan-400/20 text-emerald-400 border border-emerald-400/20 shadow-sm">
                      {ui('当前版本', 'CURRENT')}
                    </span>
                  )}
                  {/* 改动数量 */}
                  <span className="ml-auto px-2 py-0.5 rounded-md text-[10px] font-medium text-white/25 bg-white/[0.04] border border-white/[0.05]">
                    {entry.changes.length} {ui('项更新', 'changes')}
                  </span>
                </div>

                {/* 变更列表 */}
                <ul className="space-y-[7px]">
                  {entry.changes.map((change, cIdx) => (
                    <li
                      key={cIdx}
                      className="flex items-start gap-2.5 text-[12.5px] leading-relaxed group"
                    >
                      <span className={`mt-[7px] w-[5px] h-[5px] rounded-full flex-shrink-0 ${isFirst ? 'bg-cyan-400/40 group-hover:bg-cyan-400/60' : 'bg-white/15 group-hover:bg-white/30'} transition-colors duration-200`} />
                      <span className="text-white/55 group-hover:text-white/75 transition-colors duration-200">{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* 底部 */}
      <div className="text-center pt-6 pb-4 space-y-2">
        <p className="text-[11px] text-white/20 tracking-widest uppercase">
          {ui('— 更多精彩，敬请期待 —', '— Stay tuned for more —')}
        </p>
        <p className="text-[10px] text-white/[0.08] font-mono">
          xiaofeng-os changelog system
        </p>
      </div>
    </div>
  );
}
export const Settings = memo(SettingsImpl);
